// field types:
//   'text'      — plain text input
//   'number'    — numeric input
//   'date'      — date input (YYYY-MM-DD)
//   'flag'      — Y / N select
//   'dropdown'  — FK select from another table
//   'auto'      — system-filled, hidden from form
//   'auto-code' — system-generated code derived from PK; shown as read-only preview (not editable)

// ── Reusable validation patterns (Phase 1A) ──────────────────────────────────
export const PATTERNS = {
  // Names: letters, spaces, hyphens, apostrophes, dots only (supports O'Brien, Mary-Jane, Dr.)
  NAME: { pattern: "^[A-Za-z][A-Za-z .'\\-]{0,98}$", patternMsg: "Letters, spaces, hyphens, apostrophes and dots only (e.g. O'Brien, Mary-Jane)" },
  // Strict alphabetic only (no spaces) — for codes etc.
  ALPHA: { pattern: '^[A-Za-z]+$', patternMsg: 'Letters only — no digits, spaces, or special characters' },
  // Alphanumeric with spaces (titles, descriptions)
  ALPHANUMERIC: { pattern: "^[A-Za-z0-9 ][A-Za-z0-9 .,'\\-/&()]{0,199}$", patternMsg: 'Letters, digits, spaces and common punctuation only' },
  // Alphanumeric without special characters
  ALPHANUMERIC_NO_SPECIAL: { pattern: "^[A-Za-z0-9 ]+$", patternMsg: 'Letters, digits and spaces only (no special characters)' },
  // Numeric only (digits)
  NUMERIC: { pattern: '^[0-9]+$', patternMsg: 'Digits only — no letters or special characters' },
  // Indian mobile (10 digit, optional +91)
  MOBILE_IN: { pattern: '^(\\+91[\\-\\s]?)?[6-9][0-9]{9}$', patternMsg: 'Valid 10-digit Indian mobile (starts with 6/7/8/9), optionally prefixed with +91' },
  // Email
  EMAIL: { pattern: '^[A-Za-z0-9._%+\\-]+@[A-Za-z0-9.\\-]+\\.[A-Za-z]{2,}$', patternMsg: 'Valid email format (e.g. name@example.com)' },
  // Indian PAN
  PAN: { pattern: '^[A-Z]{5}[0-9]{4}[A-Z]$', patternMsg: 'Valid PAN — 5 letters + 4 digits + 1 letter (e.g. ABCDE1234F)' },
  // Indian TAN
  TAN: { pattern: '^[A-Z]{4}[0-9]{5}[A-Z]$', patternMsg: 'Valid TAN — 4 letters + 5 digits + 1 letter (e.g. PUNE12345A)' },
  // Indian IFSC
  IFSC: { pattern: '^[A-Z]{4}0[A-Z0-9]{6}$', patternMsg: 'Valid IFSC — 4 letters + 0 + 6 alphanumeric (e.g. HDFC0001234)' },
  // Indian fiscal year YYYY-YY
  FY: { pattern: '^(20[2-9][0-9])-([2-9][0-9])$', patternMsg: "Format YYYY-YY (e.g. 2026-27)" },
  // Aadhaar (12 digit)
  AADHAAR: { pattern: '^[0-9]{12}$', patternMsg: 'Aadhaar must be exactly 12 digits' },
  // UAN (12 digit)
  UAN: { pattern: '^[0-9]{12}$', patternMsg: 'UAN must be exactly 12 digits' },
  // Passport
  PASSPORT_IN: { pattern: '^[A-Za-z][0-9]{7}$', patternMsg: 'Valid passport number — 1 letter followed by 7 digits (e.g. N2345678)' },
  // Account number 9-18 digits
  BANK_ACCOUNT: { pattern: '^[0-9]{9,18}$', patternMsg: 'Account number must be 9-18 digits' },
  // URL
  URL: { pattern: '^https?:\\/\\/[^\\s]{4,500}$', patternMsg: 'Valid URL starting with http:// or https://' },
  // Alphanumeric reference (transaction refs, ack refs etc.)
  ALNUM_REF: { pattern: '^[A-Z0-9\\-/]{4,50}$', patternMsg: '4-50 chars: uppercase letters, digits, hyphen, slash' },
};

// Shared FK shorthand helpers
const FK = {
  company: { type: 'dropdown', label: 'Company', required: true, source: 'COMPANY', valueField: 'company_id', labelField: 'company_name', labelCombo: ['company_id', 'company_name'] },
  bg: { type: 'dropdown', label: 'Business Group', required: true, source: 'BUSINESS_GROUP', valueField: 'bg_id', labelField: 'bg_id', labelCombo: ['bg_id', 'business_group_name'] },
  ldg: { type: 'dropdown', label: 'Legislation Group', required: true, source: 'LEGISLATION_DATA_GROUP', valueField: 'ldg_id', labelField: 'legislation_name' },
  module: { type: 'dropdown', label: 'Module', required: true, source: 'MY_MODULES', valueField: 'module_id', labelField: 'module_name', autoSelectSingle: true },
  payroll: { type: 'dropdown', label: 'Payroll', required: true, source: 'PAYROLL', valueField: 'payroll_id', labelField: 'payroll_name' },
  period: { type: 'dropdown', label: 'Pay Period', required: true, source: 'PAY_PERIOD', valueField: 'period_id', labelField: 'period_name' },
  location: { type: 'dropdown', label: 'Location', required: true, source: 'LOCATION', valueField: 'location_id', labelField: 'city' },
  department: { type: 'dropdown', label: 'Department', required: false, source: 'DEPARTMENT', valueField: 'department_id', labelField: 'name' },
  designation: { type: 'dropdown', label: 'Designation', required: false, source: 'DESIGNATION', valueField: 'designation_id', labelField: 'name' },
  element: { type: 'dropdown', label: 'Element', required: true, source: 'ELEMENT_TYPE', valueField: 'element_id', labelField: 'element_name' },
  formula: { type: 'dropdown', label: 'Formula', required: false, source: 'PAYROLL_FORMULA', valueField: 'formula_id', labelField: 'formula_name' },
  consolidation: { type: 'dropdown', label: 'Consolidation Group', required: true, source: 'CONSOLIDATION_GROUP', valueField: 'consolidation_group_id', labelField: 'group_name' },
  periodStatus: { type: 'dropdown', label: 'Period Status', required: true, source: 'PAY_PERIOD_STATUS', valueField: 'status_id', labelField: 'status_name' },
  periodType: { type: 'dropdown', label: 'Period Type', required: true, source: 'PERIOD_TYPE', valueField: 'period_name', labelField: 'period_name' },
  payrollRun: { type: 'dropdown', label: 'Payroll Run', required: true, source: 'PAYROLL_RUN', valueField: 'run_id', labelField: 'run_name' },
  businessType: { type: 'dropdown', label: 'Business Type', required: true, source: 'BUSINESS_TYPE', valueField: 'business_type_id', labelField: 'business_type_name' },
  bank: { type: 'dropdown', label: 'Bank', required: false, source: 'BANK_MASTER', valueField: 'bank_id', labelField: 'bank_name' },
  workSchedule: { type: 'dropdown', label: 'Work Schedule', required: false, source: 'WORK_SCHEDULE', valueField: 'schedule_id', labelField: 'schedule_name' },
  absenceType: { type: 'dropdown', label: 'Absence Type', required: false, source: 'ABSENCE_TYPE', valueField: 'absence_type_id', labelField: 'absence_name' },
  advanceSalary: { type: 'dropdown', label: 'Advance Salary', required: true, source: 'ADVANCE_SALARY', valueField: 'loan_id', labelField: 'loan_type', labelCombo: ['loan_id', 'loan_type'] },
  advanceSalaryType: { type: 'dropdown', label: 'Advance Salary Type', required: true, source: 'ADVANCE_SALARY_TYPE', valueField: 'type_code', labelField: 'type_name', labelCombo: ['type_code', 'type_name'] },
  advanceSalaryPolicy: { type: 'dropdown', label: 'Advance Salary Policy', required: true, source: 'ADVANCE_SALARY_POLICY', valueField: 'policy_id', labelField: 'policy_name', labelCombo: ['policy_id', 'policy_name'] },
  advanceSalaryRequest: { type: 'dropdown', label: 'Advance Salary Request', required: true, source: 'ADVANCE_SALARY_REQUEST', valueField: 'request_id', labelField: 'request_id', labelCombo: ['request_id', 'employee_name', 'loan_type'] },
  balanceDef: { type: 'dropdown', label: 'Balance Definition', required: true, source: 'BALANCE_DEFINITION', valueField: 'balance_def_id', labelField: 'balance_name' },
  definedBal: { type: 'dropdown', label: 'Defined Balance', required: true, source: 'DEFINED_BALANCE', valueField: 'defined_balance_id', labelField: 'dimension_name' },
  currency: { type: 'dropdown', label: 'Currency Code', required: true, source: 'CURRENCY', valueField: 'currency_code', labelField: 'currency_name', labelCombo: ['currency_code', 'currency_name'], filterFn: (r) => r.active_flag === 'Y' },
  balanceCategory: { type: 'dropdown', label: 'Balance Category', required: true, source: 'BALANCE_CATEGORY', valueField: 'category_code', labelField: 'category_name', filterFn: (r) => r.status === 'Active' },
  assignment: {
    type: 'dropdown', label: 'Assignment', required: true, source: 'ASSIGNMENT', valueField: 'assignment_id', labelField: 'employee_code', labelCombo: ['assignment_id', 'full_name', 'employee_code'],
    chain: { via: 'person_id', source: 'PERSON', keyField: 'person_id', labelField: 'full_name' },
    includeInactive: true
  },
  person: { type: 'dropdown', label: 'Person', required: true, source: 'PERSON', valueField: 'person_id', labelField: 'full_name', labelCombo: ['person_id', 'full_name'] },
  personalPaymentMethod: { type: 'dropdown', label: 'Payment Method', required: true, source: 'PERSONAL_PAYMENT_METHOD', valueField: 'bank_account_id', labelField: 'display_name' },
  // Phase 2 lookup masters
  assignmentStatus: { type: 'dropdown', label: 'Assignment Status', required: true, source: 'ASSIGNMENT_STATUS_MASTER', valueField: 'status_code', labelField: 'status_name' },
  employmentType: { type: 'dropdown', label: 'Employment Type', required: true, source: 'EMPLOYMENT_TYPE_MASTER', valueField: 'type_code', labelField: 'type_name' },
  relationshipType: { type: 'dropdown', label: 'Relationship Type', required: true, source: 'RELATIONSHIP_TYPE_MASTER', valueField: 'rel_code', labelField: 'rel_name' },
  proofType: { type: 'dropdown', label: 'Proof Type', required: true, source: 'PROOF_TYPE_MASTER', valueField: 'proof_code', labelField: 'proof_name', labelCombo: ['proof_code', 'proof_name'] },
  taxDeclStatus: { type: 'dropdown', label: 'Status', required: true, source: 'TAX_DECLARATION_STATUS_MASTER', valueField: 'status_code', labelField: 'status_name' },
  form16GenStatus: { type: 'dropdown', label: 'Generation Status', required: true, source: 'FORM16_GENERATION_STATUS_MASTER', valueField: 'status_code', labelField: 'status_name' },
  grade: { type: 'dropdown', label: 'Grade', required: false, source: 'GRADE_MASTER', valueField: 'grade_code', labelField: 'grade_name', labelCombo: ['grade_code', 'grade_name'] },
};

const AUTO = { created_by: { type: 'auto', autoValue: 'SYSTEM' }, updated_by: { type: 'auto', autoValue: 'ADMIN' } };
const DATES = {
  effective_from: { type: 'date', label: 'Effective From', required: true },
  effective_to: { type: 'date', label: 'Effective To', required: true },
};
const STATUS = { active_flag: { type: 'flag', label: 'Status', required: true } };

export const TABLE_CONFIG = {

  // ══ ORG STRUCTURE ═══════════════════════════════════════════════════════════

  CURRENCY: {
    pk: 'currency_id', pkPrefix: 'CUR', pkPad: 3, label: 'Currency', showPk: true,
    displayColumns: ['currency_id', 'currency_code', 'currency_name', 'symbol', 'decimal_places', 'country', 'active_flag'],
    formNotice: {
      severity: 'info',
      title: 'Phase 7 — Country auto-fill',
      message: 'Select a Country first — Currency Code, Currency Name, Symbol, and Decimal Places will auto-fill from the country master. You can still override any value manually.',
    },
    sections: [
      { id: 'country', label: 'Country (auto-fill source)', fields: ['country'], columns: 1 },
      { id: 'basic', label: 'Currency Details', fields: ['currency_code', 'currency_name', 'symbol', 'decimal_places'], columns: 2 },
      { id: 'dates', label: 'Effective Period', fields: ['effective_from', 'active_flag', 'effective_to'], columns: 3 },
    ],
    fields: {
      country: {
        type: 'country-select', label: 'Country', required: true,
        autoFill: { currency_code: 'currency', currency_name: 'name', symbol: 'symbol', decimal_places: 'decimals' },
        hint: 'Selecting country auto-fills the 4 currency fields below'
      },
      currency_code: {
        type: 'text', label: 'Currency Code', required: true, maxLength: 3,
        pattern: '^[A-Z]{3}$', patternMsg: 'ISO 4217 — exactly 3 uppercase letters, e.g. INR, USD',
        autoUppercase: true,
        hint: 'Auto-filled from country (override if needed)'
      },
      currency_name: {
        type: 'text', label: 'Currency Name', required: true, maxLength: 50,
        ...PATTERNS.NAME,
        hint: 'Auto-filled from country (e.g. Indian Rupee)'
      },
      symbol: {
        type: 'text', label: 'Symbol', required: false, maxLength: 5,
        pattern: "^[^A-Za-z0-9]{1,5}$", patternMsg: 'Symbol must be 1-5 non-alphanumeric characters (₹, $, €, £)',
        hint: 'Auto-filled from country (e.g. ₹, $, €)'
      },
      decimal_places: {
        type: 'number', label: 'Decimal Places', required: false, min: 0, max: 3, integerOnly: true,
        hint: 'Most currencies use 2 (paise/cents). JPY/KRW = 0, KWD = 3.'
      },
      ...STATUS,
      effective_from: DATES.effective_from,
      ...AUTO,
    },
  },

  LEGISLATION_DATA_GROUP: {
    pk: 'ldg_id', pkPrefix: 'LDG', pkPad: 5, label: 'Legislation Data Group',
    formNotice: {
      severity: 'info',
      title: 'Phase 7 — Country-driven defaults',
      message: 'Select Country first — Currency Code auto-fills from country master. Set FY Start Month; End Month auto-derives to make a 12-month financial year.',
    },
    sections: [
      { id: 'country', label: 'Country (auto-fill source)', fields: ['country'], columns: 1 },
      { id: 'basic', label: 'Basic Details', fields: ['legislation_name', 'currency_code', 'legislation_code'], columns: 2 },
      { id: 'financial', label: 'Financial Settings', fields: ['fiscal_year_start_month', 'fiscal_year_end_month'], columns: 2 },
      { id: 'compliance', label: 'Compliance Settings', fields: ['pf_applicable', 'esi_applicable', 'pt_applicable', 'tds_applicable', 'gratuity_applicable', 'bonus_applicable'], columns: 3 },
      { id: 'dates', label: 'Dates', fields: ['effective_from'], columns: 2 },
      { id: 'metadata', label: 'Status', fields: ['active_flag'], columns: 2 },
    ],
    fields: {
      country: {
        type: 'country-select', label: 'Country', required: true,
        autoFill: { currency_code: 'currency' },
        hint: 'Selecting country auto-fills Currency Code below. Statutory flags should be set based on country regulations.'
      },
      legislation_name: {
        type: 'text', label: 'Legislation Name', required: true, maxLength: 100,
        ...PATTERNS.NAME,
        hint: 'e.g. India Payroll Legislation'
      },
      legislation_code: { type: 'auto-code', label: 'Legislation Code', codePrefix: 'LEG-', codePad: 5 },
      currency_code: {
        ...FK.currency, required: true,
        hint: 'Auto-filled when Country is selected'
      },
      fiscal_year_start_month: {
        type: 'month', label: 'FY Start Month', required: true,
        fyAutoCalcTo: 'fiscal_year_end_month',
        hint: 'Select start month; End month auto-derives to previous month (12-month cycle)'
      },
      fiscal_year_end_month: {
        type: 'month', label: 'FY End Month', required: true,
        hint: 'Auto-calculated from Start Month (e.g. April start → March end)'
      },
      pf_applicable: { type: 'flag', label: 'PF Applicable', required: true },
      esi_applicable: { type: 'flag', label: 'ESI Applicable', required: true },
      pt_applicable: { type: 'flag', label: 'PT Applicable', required: true },
      tds_applicable: { type: 'flag', label: 'TDS Applicable', required: true },
      gratuity_applicable: { type: 'flag', label: 'Gratuity Applicable', required: true },
      bonus_applicable: { type: 'flag', label: 'Bonus Applicable', required: true },
      ...STATUS,
      effective_from: DATES.effective_from,
      ...AUTO,
    },
  },

  BUSINESS_TYPE: {
    pk: 'business_type_id', pkPrefix: 'BT', pkPad: 5, label: 'Business Type',
    sections: [
      { id: 'basic', label: 'Business Type Details', fields: ['business_type_name'], columns: 1 },
      { id: 'references', label: 'Org Structure (Multi-select)', fields: ['department_ids', 'designation_ids'], columns: 2 },
      { id: 'dates', label: 'Effective Period', fields: ['effective_from', 'effective_to'], columns: 2 },
      { id: 'metadata', label: 'Status', fields: ['active_flag'], columns: 2 },
    ],
    fields: {
      business_type_name: {
        type: 'text', label: 'Business Type Name', required: true,
        minLength: 2, maxLength: 100,
        pattern: '^[A-Za-z0-9 &/\\-\\.,\\(\\)]+$',
        patternMsg: 'Only letters, numbers, spaces, and & / - . , ( ) allowed'
      },
      department_ids: {
        type: 'multi-dropdown', label: 'Departments', required: true,
        source: 'DEPARTMENT', valueField: 'department_id', labelField: 'name',
        labelCombo: ['department_id', 'name'],
        minItems: 1, maxItems: 20
      },
      designation_ids: {
        type: 'multi-dropdown', label: 'Designations', required: true,
        source: 'DESIGNATION', valueField: 'designation_id', labelField: 'name',
        labelCombo: ['designation_id', 'name'],
        minItems: 1, maxItems: 20
      },
      effective_from: { type: 'date', label: 'Effective From', required: true },
      effective_to: {
        type: 'date', label: 'Effective To', required: false,
        afterField: 'effective_from'
      },
      ...STATUS,
      ...AUTO,
    },
  },

  COMPANY: {
    pk: 'company_id', pkPrefix: 'CO', pkPad: 3, label: 'Company',
    sections: [
      { id: 'basic', label: 'Basic Information', fields: ['company_name', 'business_type_id', 'authorized_person', 'website_url', 'address'], columns: 2 },
      { id: 'payroll', label: 'Payroll Defaults', fields: ['default_bg_id', 'default_ldg_id'], columns: 2 },
      { id: 'compliance', label: 'Compliance Details', fields: ['PAN', 'TAN', 'GST', 'UIN_no', 'aadhar_udyam', 'shop_act'], columns: 2 },
      { id: 'media', label: 'Media Uploads', fields: ['authorized_person_photo', 'company_logo'], columns: 2 },
      { id: 'dates', label: 'Dates', fields: ['incorporation_date', 'effective_from'], columns: 2 },
      { id: 'metadata', label: 'Status', fields: ['active_flag'], columns: 2 },
    ],
    fields: {
      company_name: {
        type: 'text', label: 'Company Name', required: true, maxLength: 100,
        pattern: "^[A-Za-z0-9][A-Za-z0-9 .,'\\-&()]{1,99}$",
        patternMsg: 'Letters, digits, spaces and common punctuation only (e.g. Tata Motors Pvt. Ltd.)'
      },
      business_type_id: { ...FK.businessType },
      default_bg_id: {
        ...FK.bg, label: 'Default Business Group', required: false,
        hint: 'Sets the default context when "Manage Payroll" is clicked'
      },
      default_ldg_id: {
        ...FK.ldg, label: 'Default Legislation Group', required: false, autoSelectSingle: true,
        filterFn: (r) => r.active_flag === 'Y',
        hint: 'India = LDG00001 — auto-selected if only one exists'
      },
      authorized_person: {
        type: 'text', label: 'Authorized Person', required: true, maxLength: 100,
        ...PATTERNS.NAME,
        hint: 'Person name — letters, spaces, hyphens, apostrophes only'
      },
      website_url: { type: 'url', label: 'Website URL', required: false, maxLength: 100 },
      address: { type: 'text', label: 'Company Address', required: false, maxLength: 500, multiline: true, rows: 3 },
      PAN: {
        type: 'text', label: 'PAN', required: true, maxLength: 10, minLength: 10,
        pattern: '^[A-Z]{5}[0-9]{4}[A-Z]$', patternMsg: 'Format: ABCDE1234F', autoUppercase: true
      },
      TAN: {
        type: 'text', label: 'TAN', required: true, maxLength: 10, minLength: 10,
        pattern: '^[A-Z]{4}[0-9]{5}[A-Z]$', patternMsg: 'Format: AAAA00000A', autoUppercase: true
      },
      GST: {
        type: 'text', label: 'GST Number', required: false, maxLength: 15, minLength: 15,
        pattern: '^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$', patternMsg: '15-char GST (e.g. 27AABCV1234D1Z5)', autoUppercase: true
      },
      UIN_no: {
        type: 'text', label: 'UIN No.', required: true, maxLength: 20,
        pattern: '^[A-Z0-9]{6,20}$',
        patternMsg: 'UIN: 6-20 uppercase alphanumeric characters',
        autoUppercase: true,
        hint: 'Unique Identification Number issued by regulator'
      },
      aadhar_udyam: {
        type: 'text', label: 'Aadhar / Udyam', required: false, maxLength: 30,
        pattern: '^[0-9]{12}$|^UDYAM-[A-Z]{2}-[0-9]{2}-[0-9]{7}$', patternMsg: '12-digit Aadhaar or UDYAM-XX-00-0000000'
      },
      shop_act: {
        type: 'text', label: 'Shop Act', required: true, maxLength: 30,
        pattern: '^[A-Z0-9\\-/]{4,30}$',
        patternMsg: 'Shop & Establishment registration number (4-30 chars: letters, digits, hyphen, slash)',
        autoUppercase: true,
        hint: 'State Shop & Establishment Act registration number'
      },
      authorized_person_photo: { type: 'image', label: 'Authorized Person Photo', required: false, accept: 'image/*', hint: 'JPG, PNG up to 5 MB' },
      company_logo: { type: 'image', label: 'Company Logo', required: false, accept: 'image/png,image/jpeg,image/svg+xml', hint: 'PNG, JPG, SVG — recommended 200×200' },
      incorporation_date: { type: 'date', label: 'Incorporation Date', required: true },
      ...STATUS,
      effective_from: DATES.effective_from,
      ...AUTO,
    },
  },

  LOCATION: {
    pk: 'location_id', pkPrefix: 'LOC', pkPad: 3, label: 'Location',
    sections: [
      { id: 'basic', label: 'Basic Location Info', fields: ['country', 'state', 'city', 'postal_code'], columns: 2 },
      { id: 'regional', label: 'Regional Settings', fields: ['currency', 'time_zone', 'country_code'], columns: 3 },
      { id: 'dates', label: 'Dates', fields: ['effective_from'], columns: 2 },
      { id: 'metadata', label: 'Status', fields: ['active_flag'], columns: 2 },
    ],
    fields: {
      country: {
        type: 'country-select', label: 'Country', required: true,
        cascade: ['state', 'city'],
        autoFill: { currency: 'currency', time_zone: 'timeZone', country_code: 'countryCode' }
      },
      state: {
        type: 'state-select', label: 'State / Province', required: true, dependsOn: 'country',
        cascade: ['city']
      },
      city: { type: 'city-select', label: 'City', required: true, dependsOn: 'state' },
      postal_code: { type: 'postal', label: 'Postal Code', required: true },
      currency: { type: 'text', label: 'Currency', required: true, maxLength: 5, readOnly: true },
      time_zone: { type: 'text', label: 'Time Zone', required: false, maxLength: 50, readOnly: true },
      country_code: { type: 'text', label: 'Country Code', required: false, maxLength: 5, readOnly: true },
      ...STATUS,
      effective_from: DATES.effective_from,
      ...AUTO,
    },
  },

  BUSINESS_GROUP: {
    pk: 'bg_id', pkPrefix: 'BG', pkPad: 3, label: 'Business Group', showPk: true,
    displayColumns: ['bg_id', 'business_group_name', 'company_id', 'location_id', 'active_flag', 'effective_from'],
    displayJoins: {
      company_id: { source: 'COMPANY', valueField: 'company_id', labelCombo: ['company_id', 'company_name'] },
      location_id: { source: 'LOCATION', valueField: 'location_id', labelCombo: ['location_id', 'city'] },
    },
    sections: [
      { id: 'basic', label: 'Basic Details', fields: ['business_group_name'], columns: 1 },
      { id: 'references', label: 'Organisation', fields: ['company_id', 'location_id'], columns: 2 },
      { id: 'metadata', label: 'Status', fields: ['active_flag', 'effective_from'], columns: 2 },
    ],
    fields: {
      business_group_name: {
        type: 'text', label: 'Business Group Name', required: true, maxLength: 100,
        pattern: "^[A-Za-z][A-Za-z .'\\-&()]{1,99}$",
        patternMsg: 'Letters, spaces and & ( ) - . \' only (no digits or other special chars)'
      },
      company_id: { ...FK.company, required: true },
      location_id: { ...FK.location, required: true },
      ...STATUS,
      effective_from: DATES.effective_from,
      ...AUTO,
    },
  },

  DEPARTMENT: {
    pk: 'department_id', pkPrefix: 'DEPT', pkPad: 3, label: 'Department',
    fields: {
      name: {
        type: 'text', label: 'Department Name', required: true, maxLength: 100,
        pattern: "^[A-Za-z][A-Za-z0-9 &/\\-]{0,99}$",
        patternMsg: 'Letters, digits, spaces and & / - only (e.g. R&D, IT/Ops)'
      },
      ...STATUS,
      effective_from: DATES.effective_from,
      ...AUTO,
    },
  },

  DESIGNATION: {
    pk: 'designation_id', pkPrefix: 'DES', pkPad: 3, label: 'Designation',
    fields: {
      name: {
        type: 'text', label: 'Designation Name', required: true, maxLength: 100,
        pattern: "^[A-Za-z][A-Za-z0-9 &/\\-\\.]{0,99}$",
        patternMsg: 'Letters, digits, spaces and & / - . only (e.g. Sr. Mgr, VP-Engg)'
      },
      ...STATUS,
      effective_from: DATES.effective_from,
      ...AUTO,
    },
  },

  MY_MODULES: {
    pk: 'module_id', pkPrefix: 'MOD', pkPad: 5, label: 'My Modules',
    fields: {
      module_name: {
        type: 'text', label: 'Module Name', required: true, maxLength: 100,
        ...PATTERNS.NAME,
        hint: 'e.g. Payroll, Workforce, Tax'
      },
      module_code: {
        type: 'auto-code', label: 'Module Code',
        codePrefix: 'PRM-MD-', codePad: 4
      },
      description: { type: 'text', label: 'Description', required: false, maxLength: 200 },
      ...STATUS,
      ...DATES,
      ...AUTO,
    },
  },

  // ══ PAY FOUNDATION ═══════════════════════════════════════════════════════════

  PERIOD_TYPE: {
    pk: 'period_type_id', pkPrefix: 'PT', pkPad: 5, label: 'Period Type', showPk: true,
    displayColumns: ['period_type_id', 'period_code', 'period_name', 'number_of_days', 'active_flag'],
    sections: [
      { id: 'identity', label: 'Period Type Definition', fields: ['period_name', 'period_code', 'number_of_days', 'description'], columns: 2 },
      { id: 'context', label: 'Module Context', fields: ['module_id'], columns: 1 },
      { id: 'meta', label: 'Status & Effective Period', fields: ['active_flag', 'effective_from', 'effective_to'], columns: 3 },
    ],
    fields: {
      period_name: {
        type: 'text', label: 'Period Type Name', required: true, maxLength: 50,
        ...PATTERNS.NAME,
        hint: 'e.g. Monthly, Weekly, Bi-Weekly, Semi-Monthly, Daily'
      },
      period_code: {
        type: 'text', label: 'Period Code', required: true, maxLength: 20,
        pattern: '^[A-Z][A-Z0-9_-]{1,19}$',
        patternMsg: 'Uppercase letters/digits/hyphens/underscores, must start with letter',
        autoUppercase: true,
        autoGenFrom: 'period_name', autoGenStyle: 'upper_underscore',
        hint: 'Auto-generated from Period Type Name (live)'
      },
      number_of_days: {
        type: 'number', label: 'Number of Days in Cycle', required: true,
        min: 1, max: 366, integerOnly: true,
        hint: 'Days per pay cycle (Daily=1, Weekly=7, Bi-Weekly=14, Monthly=30, Annual=365)'
      },
      description: { type: 'text', label: 'Description', required: false, maxLength: 200 },
      module_id: { ...FK.module },
      ...STATUS,
      ...DATES,
      ...AUTO,
    },
  },

  PAY_PERIOD_STATUS: {
    pk: 'status_id', pkPrefix: 'PPST', pkPad: 5, label: 'Pay Period Status',
    fields: {
      status_code: {
        type: 'text', label: 'Status Code', required: true, maxLength: 20,
        pattern: '^[A-Z_]+$',
        patternMsg: 'Uppercase letters and underscores only',
        autoUppercase: true,
        autoGenFrom: 'status_name', autoGenStyle: 'upper_underscore',
        hint: 'Auto-generated from Status Name (live as you type)'
      },
      status_name: {
        type: 'text', label: 'Status Name', required: true, maxLength: 50,
        ...PATTERNS.NAME,
        hint: 'e.g. Open, Closed, In Progress'
      },
      description: { type: 'text', label: 'Description', required: false, maxLength: 200 },
      module_id: { ...FK.module },
      is_active: { type: 'flag', label: 'Active', required: true },
      ...DATES,
      ...AUTO,
    },
  },

  CONSOLIDATION_GROUP: {
    pk: 'consolidation_group_id', pkPrefix: 'CG', pkPad: 3, label: 'Consolidation Group', showPk: true,
    displayColumns: ['consolidation_group_id', 'group_name', 'bg_id', 'COMPANY_id', 'ldg_id', 'active_flag'],
    displayJoins: {
      bg_id: { source: 'BUSINESS_GROUP', valueField: 'bg_id', labelCombo: ['bg_id', 'business_group_name'] },
      COMPANY_id: { source: 'COMPANY', valueField: 'company_id', labelCombo: ['company_id', 'company_name'] },
      ldg_id: { source: 'LEGISLATION_DATA_GROUP', valueField: 'ldg_id', labelField: 'legislation_name' }
    },
    sections: [
      { id: 'hierarchy', label: 'Hierarchy (cascading)', fields: ['bg_id', 'COMPANY_id', 'ldg_id'], columns: 3 },
      { id: 'basic', label: 'Group Details', fields: ['group_name', 'group_code', 'description', 'module_id'], columns: 2 },
      { id: 'meta', label: 'Status & Period', fields: ['active_flag', 'effective_from', 'effective_to'], columns: 3 },
    ],
    fields: {
      bg_id: {
        ...FK.bg, required: true,
        cascade: ['COMPANY_id', 'ldg_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company, required: true,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: {
        ...FK.ldg,
        dependsOn: 'bg_id',
        filterVia: {
          field: 'COMPANY_id',
          parentKey: 'default_bg_id',
          valueKey: 'default_ldg_id',
        },
        hint: 'Only legislation groups linked to the selected Business Group are shown'
      },
      group_name: {
        type: 'text', label: 'Group Name', required: true, maxLength: 100,
        ...PATTERNS.NAME,
        hint: 'e.g. Northern Region, Manufacturing Division'
      },
      group_code: {
        type: 'auto-code', label: 'Group Code',
        codePrefix: 'GLAM-CG-',
        dynamicPrefix: (data) => {
          if (data && data.group_name) {
            const firstWord = data.group_name.trim().split(/\s+/)[0].toUpperCase();
            return `${firstWord}-CG-`;
          }
          return 'GLAM-CG-';
        },
        codePad: 4, codeIncludeYear: true
      },
      description: { type: 'text', label: 'Description', required: false, maxLength: 500 },
      module_id: { ...FK.module },
      ...STATUS,
      ...DATES,
      ...AUTO,
    },
  },

  PAYROLL: {
    pk: 'payroll_id', pkPrefix: 'PAY', pkPad: 3, label: 'Payroll',
    formNotice: {
      severity: 'info',
      title: 'Phase 7 — Dynamic form based on Period Type',
      message: 'Select Period Type first. Fields below adapt to the selected frequency: Monthly shows cutoff/payment day, Weekly/Bi-Weekly hide month-related options, Daily strips most cycle config.',
    },
    sections: [
      { id: 'basic', label: 'Pay Schedule', fields: ['payroll_name', 'payroll_code', 'period_type', 'currency_code'], columns: 2 },
      { id: 'schedule', label: 'Schedule Config', fields: ['cutoff_day', 'payment_day', 'payment_type', 'payment_month_type', 'payment_date_offset', 'working_days', 'salary_calculation_type'], columns: 2 },
      { id: 'refs', label: 'Organisation', fields: ['bg_id', 'COMPANY_id', 'ldg_id', 'consolidation_group_id'], columns: 2 },
      { id: 'dates', label: 'Dates', fields: ['first_period_end_date', 'effective_from'], columns: 2 },
      { id: 'metadata', label: 'Status', fields: ['status', 'active_flag', 'number_of_years'], columns: 3 },
    ],
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg },
      payroll_name: {
        type: 'text', label: 'Payroll Name', required: true, maxLength: 100,
        ...PATTERNS.ALPHANUMERIC,
        hint: 'e.g. VVSPL Monthly Payroll, Mumbai Office Weekly'
      },
      payroll_code: {
        type: 'text', label: 'Payroll Code', required: true, maxLength: 20,
        pattern: '^[A-Z][A-Z0-9_-]{1,19}$',
        patternMsg: 'Uppercase letters/digits/hyphens/underscores, must start with letter',
        autoUppercase: true,
        autoGenFrom: 'payroll_name', autoGenStyle: 'upper_underscore',
        hint: 'Auto-generated from Payroll Name (live as you type)'
      },
      period_type: {
        type: 'select', label: 'Period Type', required: true,
        options: [
          { value: 'monthly', label: 'Monthly' },
          { value: 'biweekly', label: 'Bi-Weekly (every 2 weeks)' },
          { value: 'weekly', label: 'Weekly' },
          { value: 'semi_monthly', label: 'Semi-Monthly (twice a month)' },
          { value: 'daily', label: 'Daily' },
        ],
        hint: 'Drives which schedule fields appear below'
      },
      currency_code: { ...FK.currency, required: true },
      cutoff_day: {
        type: 'number', label: 'Cutoff Day of Month', required: true,
        min: 1, max: 31, integerOnly: true,
        showWhen: { field: 'period_type', in: ['monthly', 'semi_monthly'] },
        hint: 'Day of month when attendance/inputs close (e.g. 25th)'
      },
      payment_type: {
        type: 'select', label: 'Payment Type', required: true,
        options: [
          { value: 'last_working_day', label: 'Last Working Day' },
          { value: 'fixed_day', label: 'Fixed Day of Month' },
        ],
        showWhen: { field: 'period_type', in: ['monthly', 'semi_monthly', 'biweekly'] }
      },
      payment_day: {
        type: 'number', label: 'Payment Day of Month', required: false,
        min: 1, max: 31, integerOnly: true,
        showWhen: { field: 'payment_type', equals: 'fixed_day' },
        hint: 'Used only when Payment Type = Fixed Day'
      },
      payment_month_type: {
        type: 'select', label: 'Payment Month', required: true,
        options: [
          { value: 'current_month', label: 'Current Month' },
          { value: 'next_month', label: 'Next Month' },
        ],
        showWhen: { field: 'period_type', in: ['monthly', 'semi_monthly'] },
        hint: 'Pay this month\'s salary in current or next month'
      },
      payment_date_offset: {
        type: 'number', label: 'Payment Date Offset (days)', required: false,
        min: 0, max: 30, integerOnly: true,
        showWhen: { field: 'period_type', in: ['weekly', 'biweekly'] },
        hint: 'Days after period-end when payment occurs (typical: 2-5)'
      },
      working_days: {
        type: 'days-multiselect', label: 'Working Days', required: true,
        options: ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'],
        hideWhen: { field: 'period_type', equals: 'daily' }
      },
      salary_calculation_type: {
        type: 'select', label: 'Salary Calculation Type', required: true,
        options: [
          { value: 'actual_days', label: 'Actual Days (calendar)' },
          { value: 'working_days', label: 'Working Days only' },
        ],
        hideWhen: { field: 'period_type', equals: 'daily' }
      },
      status: {
        type: 'select', label: 'Status', required: true,
        options: [
          { value: 'active', label: 'Active' },
          { value: 'inactive', label: 'Inactive' },
        ]
      },
      consolidation_group_id: { ...FK.consolidation },
      first_period_end_date: {
        type: 'date', label: 'First Period End Date', required: true,
        hint: 'End date of the first payroll period — basis for all future cycles'
      },
      number_of_years: {
        type: 'number', label: 'Number of Years', required: true,
        min: 1, max: 10, integerOnly: true,
        hint: 'How many years of pay periods to auto-generate'
      },
      ...STATUS,
      ...DATES,
      ...AUTO,
    },
  },

  PAY_PERIOD: {
    pk: 'period_id', pkPrefix: 'PP', pkPad: 3, label: 'Pay Period',
    fields: {
      payroll_id: { ...FK.payroll },
      period_name: {
        type: 'text', label: 'Period Name', required: true, maxLength: 100,
        hint: 'Auto-derived from Payroll + period dates (Phase 3)'
      },
      fiscal_year: {
        type: 'text', label: 'Fiscal Year', required: true, maxLength: 10,
        ...PATTERNS.FY,
        hint: 'Indian FY: April-March (e.g. 2026-27)'
      },
      start_date: { type: 'date', label: 'Start Date', required: true },
      end_date: { type: 'date', label: 'End Date', required: true, afterField: 'start_date' },
      payment_date: {
        type: 'date', label: 'Payment Date', required: true,
        hint: 'Must be on or after End Date'
      },
      status: { type: 'select', label: 'Status', required: true, options: ['pending', 'processed', 'paid'] },
      created_at: { type: 'auto', autoValue: () => new Date().toISOString().split('T')[0] },
      updated_at: { type: 'auto', autoValue: () => new Date().toISOString().split('T')[0] },
    },
  },

  // ══ ELEMENT SETUP ════════════════════════════════════════════════════════════

  ELEMENT_TYPE: {
    pk: 'element_id', pkPrefix: 'EL', pkPad: 3, label: 'Salary Structure (Component)',
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg },
      element_code: {
        type: 'text', label: 'Element Code', required: true, maxLength: 20,
        pattern: '^[A-Z][A-Z0-9_]{1,19}$',
        patternMsg: 'Uppercase letters/digits/underscores, must start with letter',
        autoUppercase: true,
        autoGenFrom: 'element_name', autoGenStyle: 'upper_underscore',
        hint: 'Auto-generated from Element Name (live as you type)'
      },
      element_name: {
        type: 'text', label: 'Element Name', required: true, maxLength: 100,
        ...PATTERNS.NAME,
        hint: 'e.g. Basic Pay, HRA, Special Allowance'
      },
      primary_classification: { type: 'text', label: 'Primary Classification', required: true, maxLength: 50 },
      secondary_classification: { type: 'text', label: 'Secondary Classification', required: false, maxLength: 50 },
      processing_priority: { type: 'number', label: 'Processing Priority', required: true, min: 1, max: 999, integerOnly: true },
      calculation_rule: { type: 'text', label: 'Calculation Rule', required: true, maxLength: 50 },
      formula_reference: { type: 'text', label: 'Formula Reference', required: false, maxLength: 50 },
      recurring_flag: { type: 'flag', label: 'Recurring', required: true },
      prorate_flag: { type: 'flag', label: 'Prorate', required: true },
      statutory_flag: { type: 'flag', label: 'Statutory', required: true },
      process_once_period: { type: 'flag', label: 'Process Once/Period', required: true },
      multiple_entries_ok: { type: 'flag', label: 'Multiple Entries OK', required: true },
      retroactive_flag: { type: 'flag', label: 'Retroactive', required: true },
      output_currency: { type: 'text', label: 'Output Currency', required: true, maxLength: 5 },
      gl_account_id: { type: 'dropdown', label: 'GL Account', required: false, source: 'GL_ACCOUNT_MASTER', valueField: 'gl_account_id', labelField: 'account_name' },
      ...STATUS,
      ...DATES,
      ...AUTO,
    },
  },

  ELEMENT_INPUT_VALUE: {
    pk: 'input_value_id', pkPrefix: 'IV', pkPad: 3, label: 'Element Input Value',
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg },
      element_id: { ...FK.element },
      input_value_name: { type: 'text', label: 'Input Value Name', required: true, maxLength: 50 },
      display_sequence: { type: 'number', label: 'Display Sequence', required: true, min: 1, max: 99, integerOnly: true },
      unit_of_measure: { type: 'text', label: 'Unit of Measure', required: false, maxLength: 20 },
      special_purpose: { type: 'text', label: 'Special Purpose', required: false, maxLength: 50 },
      displayed_flag: { type: 'flag', label: 'Displayed', required: true },
      allow_user_entry: { type: 'flag', label: 'Allow User Entry', required: true },
      required_flag: { type: 'flag', label: 'Required', required: true },
      default_value: { type: 'text', label: 'Default Value', required: false, maxLength: 50 },
      min_value: { type: 'number', label: 'Min Value', required: false },
      max_value: { type: 'number', label: 'Max Value', required: false },
      ...DATES,
      ...AUTO,
    },
  },

  ELEMENT_ELIGIBILITY: {
    pk: 'eligibility_id', pkPrefix: 'EG', pkPad: 3, label: 'Element Eligibility',
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg },
      element_id: { ...FK.element },
      payroll_id: { ...FK.payroll, required: false },
      employment_category: { type: 'text', label: 'Employment Category', required: false, maxLength: 50 },
      link_type: { type: 'text', label: 'Link Type', required: false, maxLength: 30 },
      default_value: { type: 'text', label: 'Default Value', required: false, maxLength: 50 },
      costing_option: { type: 'text', label: 'Costing Option', required: false, maxLength: 30 },
      allow_costing: { type: 'flag', label: 'Allow Costing', required: true },
      ...STATUS,
      ...DATES,
      ...AUTO,
    },
  },

  PAYROLL_FORMULA: {
    pk: 'formula_id', pkPrefix: 'FRM', pkPad: 2, label: 'Payroll Formula',
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg },
      formula_code: {
        type: 'text', label: 'Formula Code', required: true, maxLength: 30,
        pattern: '^[A-Z][A-Z0-9_]{1,29}$',
        patternMsg: 'Uppercase letters/digits/underscores, must start with letter',
        autoUppercase: true,
        autoGenFrom: 'formula_name', autoGenStyle: 'upper_underscore',
        hint: 'Auto-generated from Formula Name (live as you type)'
      },
      formula_name: {
        type: 'text', label: 'Formula Name', required: true, maxLength: 100,
        ...PATTERNS.NAME,
        hint: 'e.g. HRA Calculation, PT Slab Mumbai'
      },
      formula_type: { type: 'text', label: 'Formula Type', required: true, maxLength: 20 },
      percentage_value: { type: 'text', label: 'Percentage Value', required: false, maxLength: 10 },
      slab_definition: { type: 'text', label: 'Slab Definition', required: false, maxLength: 500 },
      max_ceiling: { type: 'text', label: 'Max Ceiling', required: false, maxLength: 20 },
      formula_expression: { type: 'text', label: 'Formula Expression', required: true, maxLength: 500 },
      ...STATUS,
      ...DATES,
      ...AUTO,
    },
  },

  // ══ SALARY & ENTRIES ═════════════════════════════════════════════════════════

  SALARY: {
    pk: 'salary_id', pkPrefix: 'SAL', pkPad: 2, label: 'Salary', showPk: true,
    displayColumns: ['salary_id', 'assignment_id', 'template_id', 'annual_ctc', 'gross_monthly', 'version_no', 'is_current', 'salary_status'],
    displayJoins: {
      COMPANY_id: { source: 'COMPANY', valueField: 'company_id', labelCombo: ['company_id', 'company_name'] },
      bg_id: { source: 'BUSINESS_GROUP', valueField: 'bg_id', labelCombo: ['bg_id', 'business_group_name'] },
      ldg_id: { source: 'LEGISLATION_DATA_GROUP', valueField: 'ldg_id', labelCombo: ['ldg_id', 'legislation_name'] },
      template_id: { source: 'SALARY_TEMPLATE', valueField: 'template_id', labelCombo: ['template_id', 'template_name'] },
      payroll_id: { source: 'PAYROLL', valueField: 'payroll_id', labelCombo: ['payroll_id', 'payroll_name'] },
      assignment_id: {
        source: 'ASSIGNMENT', valueField: 'assignment_id', labelCombo: ['assignment_id', 'employee_code'],
        chain: { via: 'person_id', source: 'PERSON', keyField: 'person_id', labelField: 'full_name' }
      },
      approved_by: { source: 'PERSON', valueField: 'person_id', labelCombo: ['person_id', 'full_name'] },
    },
    sections: [
      { id: 'org', label: 'Organisation', fields: ['bg_id', 'COMPANY_id', 'ldg_id'], columns: 3 },
      { id: 'links', label: 'Salary Setup', fields: ['template_id', 'payroll_id', 'assignment_id'], columns: 3 },
      { id: 'amounts', label: 'Compensation', fields: ['salary_basis', 'annual_ctc', 'gross_monthly', 'currency'], columns: 2 },
      { id: 'versioning', label: 'Versioning', fields: ['version_no', 'is_current', 'salary_status'], columns: 3 },
      { id: 'approval', label: 'Approval', fields: ['approval_status', 'approved_by', 'approved_at', 'revision_reason'], columns: 2 },
      { id: 'dates', label: 'Effective Period', fields: ['effective_from', 'effective_to'], columns: 2 },
    ],
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg },
      template_id: {
        type: 'dropdown', label: 'Salary Template', required: true,
        source: 'SALARY_TEMPLATE', valueField: 'template_id', labelField: 'template_name',
        labelCombo: ['template_id', 'template_name']
      },
      payroll_id: { ...FK.payroll },
      assignment_id: {
        type: 'dropdown', label: 'Assignment', required: true,
        source: 'ASSIGNMENT', valueField: 'assignment_id', labelField: 'employee_code',
        labelCombo: ['assignment_id', 'full_name', 'employee_code']
      },
      salary_basis: {
        type: 'select', label: 'Salary Basis', required: true,
        options: ['Monthly', 'Annual', 'Hourly', 'Daily'].map(v => ({ value: v, label: v }))
      },
      annual_ctc: {
        type: 'number', label: 'Annual CTC (₹)', required: true, min: 0, max: 99999999,
        hint: 'Total annual cost to company (max ₹9.99 crore)'
      },
      gross_monthly: {
        type: 'number', label: 'Gross Monthly (₹)', required: false, min: 0, max: 9999999,
        hint: 'Cached for display — derived from annual CTC'
      },
      currency: { ...FK.currency, required: true },
      version_no: { type: 'number', label: 'Version No', required: false, min: 1, max: 999, integerOnly: true },
      is_current: { type: 'flag', label: 'Is Current', required: true },
      salary_status: {
        type: 'select', label: 'Salary Status', required: true,
        options: ['draft', 'pending', 'active', 'superseded', 'inactive'].map(v => ({ value: v, label: v }))
      },
      approval_status: {
        type: 'select', label: 'Approval Status', required: true,
        options: ['Pending', 'Approved', 'Rejected'].map(v => ({ value: v, label: v }))
      },
      approved_by: {
        type: 'dropdown', label: 'Approved By', required: false,
        source: 'PERSON', valueField: 'person_id', labelField: 'full_name',
        labelCombo: ['person_id', 'full_name']
      },
      approved_at: { type: 'date', label: 'Approved At', required: false },
      revision_reason: { type: 'text', label: 'Revision Reason', required: false, maxLength: 100 },
      ...DATES,
      ...AUTO,
    },
  },

  ELEMENT_ENTRY: {
    pk: 'entry_id', pkPrefix: 'ENT', pkPad: 3, label: 'Element Entry',
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg },
      assignment_id: { ...FK.assignment },
      element_id: { ...FK.element },
      period_id: { ...FK.period },
      pay_value: { type: 'number', label: 'Pay Value', required: false, min: 0 },
      entry_type: { type: 'text', label: 'Entry Type', required: true, maxLength: 20 },
      override_flag: { type: 'flag', label: 'Override', required: true },
      reason: { type: 'text', label: 'Reason', required: false, maxLength: 200 },
      ...DATES,
      ...AUTO,
    },
  },

  ELEMENT_ENTRY_VALUE: {
    pk: 'entry_value_id', pkPrefix: 'EV', pkPad: 3, label: 'Element Entry Value',
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first - Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg },
      assignment_id: { ...FK.assignment },
      screen_entry_value: { type: 'text', label: 'Entry Value', required: true, maxLength: 100 },
      ...DATES,
      ...AUTO,
    },
  },

  SALARY_REVISION_HISTORY: {
    pk: 'revision_id', pkPrefix: 'REV', pkPad: 3, label: 'Salary Revision History',
    sections: [
      { id: 'org', label: 'Organisation', fields: ['bg_id', 'COMPANY_id', 'ldg_id'], columns: 3 },
      { id: 'source', label: 'Source Salary', fields: ['assignment_id', 'person_id', 'salary_id', 'department_name', 'designation_name', 'current_template_name', 'current_ctc'], columns: 2 },
      { id: 'previous', label: 'Previous Amounts', fields: ['prev_basic', 'prev_gross'], columns: 2 },
      { id: 'new', label: 'New Amounts', fields: ['new_template_id', 'new_basic', 'new_gross', 'increment_pct'], columns: 2 },
      { id: 'approval', label: 'Approval & Tracking', fields: ['revision_type', 'new_designation_id', 'revised_by', 'effective_date', 'revision_reason', 'revision_status', 'approved_by', 'approved_at', 'rejected_reason', 'new_salary_ref_id', 'payroll_run_applied'], columns: 2 }
    ],
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first - Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg },
      assignment_id: {
        ...FK.assignment,
        autoSelectFrom: { salary_id: 'assignment_id' },
        autoFillFromFk: { person_id: 'person_id', department_name: 'department_name', designation_name: 'designation_name' },
        apiParams: { activePayrollOnly: 'true' }
      },
      person_id: {
        ...FK.person,
        label: 'Person',
        readOnly: true,
        hint: 'Auto-fetched from Assignment'
      },
      department_name: { type: 'text', label: 'Department', readOnly: true },
      designation_name: { type: 'text', label: 'Designation', readOnly: true },
      current_template_name: {
        type: 'dropdown', label: 'Salary Template', readOnly: true,
        source: 'SALARY_TEMPLATE', valueField: 'template_id', labelField: 'template_name',
        labelCombo: ['template_id', 'template_name']
      },
      current_ctc: { type: 'number', label: 'Current CTC', readOnly: true },
      salary_id: {
        type: 'dropdown', label: 'Salary', required: true,
        source: 'SALARY', valueField: 'salary_id', labelField: 'salary_id',
        labelCombo: ['salary_id', 'annual_ctc'],
        emptyMsg: 'No active salary found for the selected assignment.',
        dependsOn: 'assignment_id', filterBy: 'assignment_id',
        autoFillFromFk: { prev_basic: 'annual_ctc', prev_gross: 'gross_monthly', current_template_name: 'template_id', current_ctc: 'annual_ctc' },
        hint: 'Pick the Salary being revised - Previous Basic/Gross auto-fill'
      },
      new_template_id: {
        type: 'dropdown', label: 'New Salary Template', required: true,
        source: 'SALARY_TEMPLATE', valueField: 'template_id', labelField: 'template_name',
        labelCombo: ['template_id', 'template_name'],
        autoFillFromFk: { new_basic: 'annual_ctc', new_gross: 'annual_ctc' },
        hint: 'Pick a template - Annual CTC will auto-fill New Basic/Gross',
        badgeFn: (row, formData) => (formData.current_template_name && row.template_name === formData.current_template_name) ? 'Current' : null
      },
      new_designation_id: {
        ...FK.designation, required: false, label: 'New Designation',
        hint: 'Select the new designation (if changed)'
      },
      prev_basic: {
        type: 'number', label: 'Previous Basic (₹)', required: true, min: 0, max: 9999999,
        readOnly: true,
        hint: 'Auto-populated from selected Salary'
      },
      new_basic: { type: 'number', label: 'New Basic (₹)', required: true, min: 0, max: 9999999 },
      prev_gross: {
        type: 'number', label: 'Previous Gross (₹)', required: false, min: 0, max: 9999999,
        readOnly: true,
        hint: 'Auto-populated from selected Salary'
      },
      new_gross: {
        type: 'number', label: 'New Gross (₹)', required: false, min: 0, max: 9999999,
        readOnly: true,
        hint: 'Auto-calculated as New Annual CTC / 12'
      },
      revision_type: {
        type: 'select', label: 'Revision Type', required: true,
        options: ['Annual Hike', 'Promotion', 'Demotion', 'Adjustment', 'Correction', 'Market Correction', 'Performance Bonus', 'Other'].map(v => ({ value: v, label: v })),
        hint: 'Reason category for this revision'
      },
      revised_by: {
        type: 'dropdown', label: 'Revised By (Person)', required: false,
        source: 'PERSON', valueField: 'person_id', labelField: 'full_name',
        labelCombo: ['person_id', 'full_name'],
        hint: 'Person who approved/recorded this revision',
        excludeIfMatches: 'person_id',
        apiParams: { activePayrollOnly: 'true' }
      },
      increment_pct: {
        type: 'number', label: 'Increment %', required: false, min: -100, max: 200,
        readOnly: true,
        hint: 'Negative = decrease, positive = increase (Auto-calculated)'
      },
      effective_date: {
        type: 'date', label: 'Effective Date', required: true,
        hint: 'Date when revision takes effect'
      },
      revision_status: {
        type: 'select', label: 'Revision Status', required: false,
        options: ['Pending Approval', 'Approved', 'Rejected'].map(v => ({ value: v, label: v })),
        defaultValue: 'Pending Approval'
      },
      approved_by: {
        type: 'dropdown', label: 'Approved By', required: false,
        source: 'PERSON', valueField: 'person_id', labelField: 'full_name',
        labelCombo: ['person_id', 'full_name'],
        apiParams: { activePayrollOnly: 'true' }
      },
      approved_at: { type: 'date', label: 'Approved At', required: false },
      rejected_reason: { type: 'text', label: 'Rejected Reason', required: false, maxLength: 200 },
      new_salary_ref_id: { type: 'text', label: 'New Salary Ref ID', required: false, maxLength: 50 },
      payroll_run_applied: {
        type: 'dropdown', label: 'Payroll Run Applied', required: false,
        source: 'PAY_PERIOD', valueField: 'period_id', labelField: 'period_name',
        filterFn: (row) => row.status !== 'closed' && row.status !== 'Closed' && row.status !== 'Inactive' && row.status !== 'inactive'
      },
      ...DATES,
      ...AUTO,
    },
  },

  // ══ WORKFORCE ════════════════════════════════════════════════════════════════

  PAYROLL_RELATIONSHIP: {
    pk: 'prel_id', pkPrefix: 'PREL', pkPad: 5, label: 'Payroll Relationship', showPk: true,
    displayColumns: ['prel_id', 'person_id', 'legal_employer_id', 'payroll_relationship_type', 'start_date', 'notice_start_date', 'end_date', 'active_flag', 'ff_status'],
    // Banner shown above the form — discourages routine manual entry
    formNotice: {
      severity: 'info',
      title: 'Manual entry is for special cases only',
      message: 'Payroll Relationships are auto-created when you add an Assignment. Use this form only for: ' +
        '(1) changing relationship type mid-employment, (2) multi-Legal-Entity scenarios, ' +
        '(3) terminations, or (4) audit corrections.',
    },
    sections: [
      { id: 'references', label: 'Organisation', fields: ['bg_id', 'COMPANY_id', 'ldg_id'], columns: 3 },
      { id: 'binding', label: 'Statutory Binding', fields: ['assignment_id', 'person_id', 'legal_employer_id', 'payroll_relationship_type'], columns: 2 },
      { id: 'period', label: 'Employment Period', fields: ['start_date', 'termination_reason', 'resignation_date', 'notice_start_date', 'end_date', 'notice_period'], columns: 2 },
      { id: 'service', label: 'Service Summary', fields: ['__service_period__'], columns: 1, editOnly: true },
      { id: 'versioning', label: 'Record Validity (Versioning)', fields: ['effective_from', 'effective_to'], columns: 2 },
      { id: 'status', label: 'Status', fields: ['active_flag', 'ff_status'], columns: 2 },
    ],
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg },
      assignment_id: {
        ...FK.assignment,
        required: true,
        editReadonly: true,
        autoFillFromFk: {
          person_id: 'person_id',
          notice_period: 'notice_period_days'
        },
        hint: 'Select an assignment to auto-fill the person and notice period'
      },
      person_id: {
        ...FK.person,
        readOnly: true,
        hint: 'Auto-populated based on the selected Assignment'
      },
      legal_employer_id: {
        type: 'text', label: 'Legal Employer ID', required: false,
        maxLength: 20,
        mirrorField: 'COMPANY_id',
        hint: 'Auto-fills to Company ID when blank. Set differently only for multi-Legal-Entity setups.'
      },
      payroll_relationship_type: {
        ...FK.relationshipType, required: true,
        defaultValue: 'STANDARD',
        hint: 'Sourced from Relationship Type Master (Phase 2). Determines tax treatment + statutory applicability.'
      },
      start_date: {
        type: 'date', label: 'Start Date', required: true,
        hint: 'When person started employment (real-world)'
      },
      resignation_date: {
        type: 'date', label: 'Resignation Date', required: false,
        hint: 'Date when resignation was submitted'
      },
      end_date: {
        type: 'date', label: 'End Date / Exit Date', required: false,
        afterField: 'start_date',
        hint: 'Auto-calculated from Notice Start Date + Notice Period, or manually filled.',
        recomputeOnChange: true,
        compute: (state) => {
          const startDateStr = state.notice_start_date || state.resignation_date;
          if (startDateStr && state.notice_period !== undefined && state.notice_period !== '') {
            const d = new Date(startDateStr);
            d.setDate(d.getDate() + Number(state.notice_period));
            return d.toISOString().split('T')[0];
          }
          return state.end_date;
        }
      },
      notice_start_date: {
        type: 'date', label: 'Notice Start Date', required: false,
        hint: 'Date the notice period formally begins',
        recomputeOnChange: true
      },
      notice_period: {
        type: 'number', label: 'Notice Period (Days)', required: false,
        hint: 'Auto-fetched from assignment, but can be overridden.',
        recomputeOnChange: true
      },
      termination_reason: {
        type: 'select', label: 'Termination Reason', required: false,
        dynamicRequired: (state) => !!state.resignation_date || !!state.end_date,
        options: [
          { value: '', label: '— Not Terminated —' },
          { value: 'Resignation', label: 'Resignation' },
          { value: 'Retirement', label: 'Retirement' },
          { value: 'Termination', label: 'Termination by Company' },
          { value: 'Death', label: 'Death (gratuity tax-free)' },
          { value: 'Layoff', label: 'Layoff / Redundancy' },
          { value: 'Absconding', label: 'Absconding' },
          { value: 'End of Contract', label: 'End of Contract' },
          { value: 'Transfer Out', label: 'Transfer to another LE' },
        ],
        hint: 'Required only when End Date is set'
      },
      __service_period__: {
        type: 'computed', label: 'Service Period',
        compute: (form) => {
          if (!form.start_date) return 'Not yet started';
          const start = new Date(form.start_date);
          const end = form.end_date ? new Date(form.end_date) : new Date();
          if (isNaN(start.getTime())) return 'Invalid start date';
          let years = end.getFullYear() - start.getFullYear();
          let months = end.getMonth() - start.getMonth();
          let days = end.getDate() - start.getDate();
          if (days < 0) { months--; days += 30; }
          if (months < 0) { years--; months += 12; }
          const parts = [];
          if (years) parts.push(`${years} year${years > 1 ? 's' : ''}`);
          if (months) parts.push(`${months} month${months > 1 ? 's' : ''}`);
          if (days && !years) parts.push(`${days} day${days > 1 ? 's' : ''}`);
          return parts.length ? parts.join(' · ') : 'Less than a day';
        }
      },
      ...STATUS,
      ...DATES,
      ...AUTO,
    },
  },

  ATTENDANCE: {
    pk: 'attendance_id', pkPrefix: 'ATT', pkPad: 5, label: 'Attendance',
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg },
      person_id: { type: 'text', label: 'Person ID', required: true, maxLength: 20 },
      assignment_id: { ...FK.assignment },
      attendance_date: { type: 'date', label: 'Attendance Date', required: true },
      in_time: { type: 'text', label: 'In Time', required: false, maxLength: 10 },
      out_time: { type: 'text', label: 'Out Time', required: false, maxLength: 10 },
      actual_hours: { type: 'number', label: 'Actual Hours', required: false, min: 0, max: 24 },
      ot_hours: { type: 'number', label: 'OT Hours', required: false, min: 0 },
      attendance_status: { type: 'text', label: 'Status', required: true, maxLength: 20 },
      remarks: { type: 'text', label: 'Remarks', required: false, maxLength: 200 },
      period_id: { ...FK.period, required: false },
      ...DATES,
      ...AUTO,
    },
  },

  LEAVE_BALANCE: {
    pk: 'leave_bal_id', pkPrefix: 'LB', pkPad: 5, label: 'Leave Balance',
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg },
      person_id: { type: 'text', label: 'Person ID', required: true, maxLength: 20 },
      assignment_id: { ...FK.assignment },
      absence_type_id: { ...FK.absenceType },
      fiscal_year: { type: 'text', label: 'Fiscal Year', required: true, maxLength: 10 },
      opening_balance: { type: 'number', label: 'Opening Balance', required: true, min: 0 },
      accrued: { type: 'number', label: 'Accrued', required: false, min: 0 },
      availed: { type: 'number', label: 'Availed', required: false, min: 0 },
      closing_balance: { type: 'number', label: 'Closing Balance', required: false, min: 0 },
      ...DATES,
      ...AUTO,
    },
  },

  // ══ TAX ══════════════════════════════════════════════════════════════════════

  TAX_DECLARATION: {
    pk: 'declaration_id', pkPrefix: 'TDEC', pkPad: 5, label: 'Tax Declaration', showPk: true,
    displayColumns: ['declaration_id', 'assignment_id', 'financial_year', 'section_code', 'declared_amount', 'approved_amount', 'status', 'effective_from'],
    sections: [
      { id: 'references', label: 'Organisation', fields: ['bg_id', 'COMPANY_id', 'ldg_id'], columns: 3 },
      { id: 'employee', label: 'Employee & Period', fields: ['assignment_id', 'financial_year'], columns: 2 },
      { id: 'investment', label: 'Investment Declaration', fields: ['section_code', 'investment_desc', 'declared_amount'], columns: 2 },
      { id: 'verify', label: 'Verification & Proof', fields: ['proof_type', 'proof_doc_ref', 'proof_submitted', 'proof_verified', 'verified_by', 'approved_amount'], columns: 2 },
      { id: 'status', label: 'Status', fields: ['status', 'rejection_reason'], columns: 2 },
      { id: 'effective', label: 'Effective Period', fields: ['effective_from', 'effective_to'], columns: 2 },
    ],
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg },
      assignment_id: { ...FK.assignment },
      financial_year: {
        type: 'text', label: 'Financial Year', required: true,
        minLength: 7, maxLength: 7,
        ...PATTERNS.FY,
        hint: 'Indian FY runs April to March (e.g. 2026-27)'
      },
      section_code: {
        type: 'select', label: 'Section Code', required: true,
        options: [
          '80C', '80CCD(1B)', '80D', '80DD',
          '80DDB', '80E', '80EE', '80EEA',
          '80EEB', '80G', '80GG', '80GGA',
          '80TTA', '80TTB', '80U',
          '24(b)', '10(13A) HRA', '10(5) LTA', '16(ii) Std Deduction',
          'Other',
        ].map(v => ({ value: v, label: v }))
      },
      investment_desc: {
        type: 'text', label: 'Investment Description', required: true,
        minLength: 3, maxLength: 200,
        pattern: "^[A-Za-z0-9 .,#\\-/()&']{3,200}$",
        patternMsg: 'Letters, digits, spaces and . , # - / ( ) & \' only',
        hint: 'e.g. LIC Policy #ABC123, HDFC ELSS Fund, PPF Account'
      },
      declared_amount: {
        type: 'number', label: 'Declared Amount (₹)', required: true,
        min: 0, max: 9999999,
        hint: 'Amount claimed by employee'
      },
      approved_amount: {
        type: 'number', label: 'Approved Amount (₹)', required: false,
        min: 0, max: 9999999,
        hint: 'Amount approved after verification (≤ declared)'
      },
      proof_type: {
        ...FK.proofType, required: false,
        hint: 'Sourced from Proof Type Master'
      },
      proof_doc_ref: {
        type: 'file-or-url', label: 'Proof Document Ref', required: false,
        hint: 'Enter a URL or upload a proof document'
      },
      proof_submitted: {
        type: 'flag', label: 'Proof Submitted', required: true,
        hint: 'Has employee uploaded proof?'
      },
      proof_verified: {
        type: 'flag', label: 'Proof Verified', required: true,
        hint: 'Has HR/Finance verified the proof?'
      },
      verified_by: {
        type: 'dropdown', label: 'Verified By (Person)', required: false,
        source: 'PERSON', valueField: 'person_id', labelField: 'full_name',
        labelCombo: ['person_id', 'full_name'],
        hint: 'Person from HR/Finance who verified the proof'
      },
      status: {
        ...FK.taxDeclStatus, required: true,
        defaultValue: 'Draft',
        hint: 'Sourced from Tax Declaration Status Master'
      },
      rejection_reason: {
        type: 'text', label: 'Rejection Reason', required: false,
        maxLength: 200,
        pattern: "^[A-Za-z0-9 .,#\\-/()&']{1,200}$|^$",
        patternMsg: 'Letters, digits, spaces and . , # - / ( ) & \' only',
        hint: 'Required if status is Rejected'
      },
      ...DATES,
      ...AUTO,
    },
  },

  FORM16_SUMMARY: {
    pk: 'form16_id', pkPrefix: 'F16', pkPad: 5, label: 'Form 16 Summary', showPk: true,
    displayColumns: ['form16_id', 'assignment_id', 'financial_year', 'tan_number', 'gross_salary', 'total_tax_payable', 'tds_deducted', 'tax_regime', 'generation_status'],
    sections: [
      { id: 'org', label: 'Organisation', fields: ['bg_id', 'COMPANY_id', 'ldg_id'], columns: 3 },
      { id: 'employee', label: 'Employee & Period', fields: ['assignment_id', 'financial_year'], columns: 2 },
      { id: 'identity', label: 'Tax Identity', fields: ['tan_number', 'pan_employee'], columns: 2 },
      { id: 'income', label: 'Income & Tax', fields: ['gross_salary', 'taxable_income', 'total_tax_payable', 'tds_deducted'], columns: 2 },
      { id: 'meta', label: 'Regime & Status', fields: ['tax_regime', 'generation_status'], columns: 2 },
      { id: 'other', label: 'Other Details', fields: ['exempt_allowances', 'standard_deduction', 'professional_tax', 'total_80c', 'total_80d', 'other_deductions', 'tax_on_income', 'surcharge', 'education_cess'], columns: 2 },
      { id: 'prev_employer', label: 'Previous Employer', fields: ['prev_employer_name', 'prev_employer_tan', 'prev_gross_salary', 'prev_tds_deducted'], columns: 2 },
      { id: 'effective', label: 'Effective Period', fields: ['status', 'effective_from', 'effective_to'], columns: 2 },
    ],
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        readOnly: true,
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        readOnly: true,
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg, readOnly: true },
      assignment_id: { ...FK.assignment },
      financial_year: {
        type: 'text', label: 'Financial Year', required: true, maxLength: 7, minLength: 7,
        ...PATTERNS.FY,
        hint: 'Indian FY: YYYY-YY (e.g. 2026-27)'
      },
      tan_number: {
        type: 'text', label: 'TAN Number', required: true, maxLength: 10, minLength: 10,
        ...PATTERNS.TAN,
        autoUppercase: true,
        readOnly: true,
        hint: 'Tax Deduction Account Number (e.g. PUNE12345A)'
      },
      pan_employee: {
        type: 'text', label: 'Employee PAN', required: true, maxLength: 10, minLength: 10,
        ...PATTERNS.PAN,
        autoUppercase: true,
        readOnly: true,
        hint: 'PAN: 5 letters + 4 digits + 1 letter (e.g. ABCDE1234F)'
      },
      gross_salary: {
        type: 'number', label: 'Gross Salary (₹)', required: true, min: 0, max: 999999999,
        readOnly: true,
        hint: 'Total annual gross salary'
      },
      taxable_income: {
        type: 'number', label: 'Taxable Income (₹)', required: true, min: 0, max: 999999999,
        readOnly: true,
        hint: 'After deductions'
      },
      exempt_allowances: { type: 'number', label: 'Exempt Allowances', required: false, min: 0, readOnly: true },
      standard_deduction: { type: 'number', label: 'Standard Deduction', required: false, min: 0, readOnly: true },
      professional_tax: { type: 'number', label: 'Professional Tax', required: false, min: 0, readOnly: true },
      total_80c: { type: 'number', label: 'Total 80c', required: false, min: 0, readOnly: true },
      total_80d: { type: 'number', label: 'Total 80d', required: false, min: 0, readOnly: true },
      other_deductions: { type: 'number', label: 'Other Deductions', required: false, min: 0, readOnly: true },
      tax_on_income: { type: 'number', label: 'Tax On Income', required: false, min: 0, readOnly: true },
      surcharge: { type: 'number', label: 'Surcharge', required: false, min: 0, readOnly: true },
      education_cess: { type: 'number', label: 'Education Cess', required: false, min: 0, readOnly: true },
      total_tax_payable: { type: 'number', label: 'Total Tax Payable (₹)', required: true, min: 0, max: 99999999, readOnly: true },
      tds_deducted: { type: 'number', label: 'TDS Deducted (₹)', required: true, min: 0, max: 99999999, readOnly: true },
      tax_regime: {
        type: 'select', label: 'Tax Regime', required: true,
        readOnly: true,
        options: [
          { value: 'OLD', label: 'Old Regime' },
          { value: 'NEW', label: 'New Regime' },
        ]
      },
      generation_status: {
        type: 'select', label: 'Generation Status', required: true,
        options: [
          { value: 'Draft', label: 'Draft' },
          { value: 'Generated', label: 'Generated' },
          { value: 'Approved', label: 'Approved' },
          { value: 'Finalized', label: 'Finalized' }
        ],
        hint: 'Select Form 16 Generation Status'
      },
      prev_employer_name: { type: 'text', label: 'Prev. Employer Name', required: false, maxLength: 100 },
      prev_employer_tan: { type: 'text', label: 'Prev. Employer TAN', required: false, maxLength: 10, ...PATTERNS.TAN, autoUppercase: true },
      prev_gross_salary: { type: 'number', label: 'Prev. Gross Salary (₹)', required: false, min: 0 },
      prev_tds_deducted: { type: 'number', label: 'Prev. TDS Deducted (₹)', required: false, min: 0 },
      status: {
        type: 'flag', label: 'Status', required: true,
        readOnly: true,
        hint: 'Toggle active status'
      },
      effective_from: { ...DATES.effective_from, readOnly: true },
      effective_to: { ...DATES.effective_to, readOnly: true },
      ...AUTO,
    },
  },

  // ══ ADVANCE SALARY STRUCTURE ═══════════════════════════════════════════════════════════════

  ADVANCE_SALARY_TYPE: {
    pk: 'type_id', pkPrefix: 'AST', pkPad: 3, label: 'Advance Salary Type', showPk: true,
    displayColumns: ['type_id', 'type_code', 'type_name', 'status', 'effective_from', 'effective_to'],
    fields: {
      type_code: {
        type: 'text', label: 'Type Code', required: true,
        autoGenFrom: 'type_name', autoGenStyle: 'short_prefix_01', autoUppercase: true,
        hint: 'Unique code (e.g., ADV_SAL_01)'
      },
      type_name: { type: 'text', label: 'Type Name', required: true, hint: 'Full name (e.g., Salary Advance)' },
      effective_from: { type: 'date', label: 'Effective From', required: true },
      effective_to: { type: 'date', label: 'Effective To', required: false },
      status: { type: 'flag', label: 'Active', required: false, defaultValue: true },
    },
  },

  ADVANCE_SALARY_POLICY: {
    pk: 'policy_id', pkPrefix: 'LP', pkPad: 5, label: 'Advance Salary Policy', showPk: true,
    displayColumns: ['policy_id', 'policy_name', 'loan_type', 'max_loan_amount', 'interest_rate', 'max_installments', 'active_flag'],
    formNotice: {
      severity: 'info',
      title: 'Loan Policy — controls eligibility, interest, and recovery rules',
      message: 'Define one policy per loan type. Policies govern who can request loans, maximum amounts, EMI count, interest rate, and approval workflow. Employees pick a policy when submitting a Loan Request.',
    },
    sections: [
      { id: 'org', label: 'Organisation', fields: ['bg_id', 'COMPANY_id', 'ldg_id'], columns: 3 },
      { id: 'identity', label: 'Policy Identity', fields: ['policy_name', 'policy_code', 'loan_type', 'description'], columns: 2 },
      { id: 'eligibility', label: 'Eligibility Rules', fields: ['min_service_months', 'min_salary_eligibility'], columns: 2 },
      { id: 'limits', label: 'Loan Limits', fields: ['max_loan_amount', 'max_loan_percentage', 'max_active_loans', 'allow_multiple_loans'], columns: 2 },
      { id: 'recovery', label: 'Recovery Configuration', fields: ['recovery_type', 'max_installments', 'min_installments', 'auto_payroll_deduction', 'allow_early_closure'], columns: 2 },
      { id: 'interest', label: 'Interest Configuration', fields: ['interest_applicable', 'interest_type', 'interest_rate', 'penal_interest_rate'], columns: 2 },
      { id: 'approval', label: 'Approval Workflow', fields: ['approval_required', 'approval_level', 'auto_approval_limit'], columns: 2 },
      { id: 'payroll', label: 'Payroll Integration', fields: ['deduction_element_id', 'payroll_frequency', 'first_deduction_rule', 'ledger_account'], columns: 2 },
      { id: 'compliance', label: 'Compliance', fields: ['attachment_mandatory', 'remarks_mandatory', 'hr_override_allowed'], columns: 2 },
      { id: 'period', label: 'Effective Period', fields: ['effective_from', 'effective_to', 'active_flag'], columns: 3 },
    ],
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg },
      policy_name: {
        type: 'text', label: 'Policy Name', required: true, maxLength: 100,
        ...PATTERNS.NAME,
        hint: 'e.g. Salary Advance Policy 2026, Festival Loan Diwali'
      },
      policy_code: {
        type: 'auto-code', label: 'Policy Code', required: true,
        codePrefix: 'SAP-', codePad: 3,
        hint: 'Auto-generated (e.g. SAP-001)'
      },
      loan_type: {
        type: 'dropdown', label: 'ADVANCE SALARY TYPE', required: true,
        source: 'ADVANCE_SALARY_TYPE',
        valueField: 'type_code',
        labelField: 'type_name',
        hint: 'Select the Advance Salary Type'
      },
      description: { type: 'text', label: 'Description', required: true, maxLength: 500 },
      min_service_months: {
        type: 'number', label: 'Min Service Period (Months)', required: true,
        min: 0, max: 240, integerOnly: true,
        hint: 'Minimum tenure required before eligible (e.g. 6 months)'
      },
      min_salary_eligibility: {
        type: 'number', label: 'Min Salary Eligibility (₹)', required: true,
        min: 0, max: 99999999,
        hint: 'Minimum monthly salary required to apply'
      },
      max_loan_amount: {
        type: 'number', label: 'Max Loan Amount (₹)', required: true,
        min: 1000, max: 99999999,
        hint: 'Cap on amount per loan under this policy'
      },
      max_loan_percentage: {
        type: 'number', label: 'Max Loan % of Salary', required: true,
        min: 0, max: 1000,
        hint: 'e.g. 300 = 3x monthly salary'
      },
      max_active_loans: {
        type: 'number', label: 'Max Active Loans Allowed', required: true,
        min: 0, max: 99, integerOnly: true,
        hint: 'How many concurrent loans an employee can have'
      },
      allow_multiple_loans: {
        type: 'flag', label: 'Allow Multiple Loans', required: true,
        hint: 'Y = employee can have more than one active loan'
      },
      recovery_type: {
        type: 'select', label: 'Recovery Type', required: true,
        options: [
          { value: 'EMI', label: 'EMI Based' },
          { value: 'FULL', label: 'Full Recovery (Lump Sum)' },
          { value: 'PERCENTAGE', label: '% of Salary' },
        ]
      },
      max_installments: {
        type: 'number', label: 'Max Installments', required: true,
        min: 1, max: 360, integerOnly: true,
        hint: '1-360 months'
      },
      min_installments: {
        type: 'number', label: 'Min Installments', required: true,
        min: 1, max: 360, integerOnly: true
      },
      auto_payroll_deduction: {
        type: 'flag', label: 'Auto Payroll Deduction', required: true,
        hint: 'Y = deducted automatically each payroll run'
      },
      allow_early_closure: {
        type: 'flag', label: 'Allow Early Closure', required: true,
        hint: 'Y = employee can pre-close before tenure ends'
      },
      interest_applicable: {
        type: 'flag', label: 'Interest Applicable', required: false,
        hint: 'Y = charge interest, N = zero-interest loan'
      },
      interest_type: {
        type: 'select', label: 'Interest Type', required: true,
        options: [
          { value: 'FLAT', label: 'Flat Rate' },
          { value: 'REDUCING', label: 'Reducing Balance' },
        ],
        hint: 'Required only if Interest Applicable = Y',
        showWhen: { field: 'interest_applicable', equals: 'Y' }
      },
      interest_rate: {
        type: 'number', label: 'Interest Rate %', required: true,
        min: 0, max: 36,
        hint: 'Annual % rate (RBI max 36% for unsecured)',
        showWhen: { field: 'interest_applicable', equals: 'Y' }
      },
      penal_interest_rate: {
        type: 'number', label: 'Penal Interest %', required: true,
        min: 0, max: 50,
        hint: 'Additional rate on late/missed EMI',
        showWhen: { field: 'interest_applicable', equals: 'Y' }
      },
      approval_required: {
        type: 'flag', label: 'Approval Required', required: true,
        hint: 'Y = needs manager/HR approval before disbursement'
      },
      approval_level: {
        type: 'select', label: 'Approval Level', required: true,
        options: [
          { value: 'MANAGER', label: 'Manager only' },
          { value: 'HR', label: 'HR only' },
          { value: 'FINANCE', label: 'Finance only' },
          { value: 'MGR_HR', label: 'Manager + HR' },
          { value: 'MGR_HR_FIN', label: 'Manager + HR + Finance' },
        ]
      },
      auto_approval_limit: {
        type: 'number', label: 'Auto Approval Limit (₹)', required: true,
        min: 0, max: 99999999,
        hint: 'Loans below this amount auto-approved without manual review'
      },
      deduction_element_id: {
        ...FK.element, required: true, label: 'Payroll Deduction Element',
        hint: 'Element used to deduct EMI from salary'
      },
      payroll_frequency: {
        type: 'select', label: 'Payroll Frequency', required: true,
        options: [
          { value: 'MONTHLY', label: 'Monthly' },
          { value: 'WEEKLY', label: 'Weekly' },
          { value: 'BIWEEKLY', label: 'Bi-weekly' },
        ]
      },
      first_deduction_rule: {
        type: 'select', label: 'First Deduction Rule', required: true,
        options: [
          { value: 'SAME_MONTH', label: 'Same Month of Disbursement' },
          { value: 'NEXT_PAYROLL', label: 'Next Payroll Cycle' },
          { value: 'NEXT_MONTH', label: 'First Day of Next Month' },
        ]
      },
      ledger_account: {
        type: 'dropdown', label: 'Ledger Account', required: true,
        source: 'GL_ACCOUNT_MASTER', valueField: 'gl_account_id', labelField: 'account_name',
        hint: 'GL account code for accounting'
      },
      attachment_mandatory: {
        type: 'flag', label: 'Attachment Mandatory', required: false,
        hint: 'Y = employee must upload supporting documents'
      },
      remarks_mandatory: { type: 'flag', label: 'Remarks Mandatory', required: false },
      hr_override_allowed: {
        type: 'flag', label: 'HR Override Allowed', required: false,
        hint: 'Y = HR can override policy limits in exceptional cases'
      },
      cost_center: { type: 'auto' },
      policy_status: { type: 'auto' },
      ...STATUS,
      ...DATES,
      ...AUTO,
    },
  },

  ADVANCE_SALARY_REQUEST: {
    pk: 'request_id', pkPrefix: 'LR', pkPad: 5, label: 'Advance Salary Request', showPk: true,
    displayColumns: ['request_id', 'assignment_id', 'policy_id', 'loan_type', 'requested_amount', 'requested_installments', 'approval_status', 'request_date', 'rejection_reason'],
    formNotice: {
      severity: 'info',
      title: 'Employee loan application',
      message: 'Employee selects a Loan Policy and submits a request. Eligibility is auto-checked against policy rules. Once approved, an active LOAN account is auto-created.',
    },
    sections: [
      { id: 'policy', label: 'Loan Policy', fields: ['policy_id', 'loan_type'], columns: 2 },
      { id: 'org', label: 'Organisation', fields: ['bg_id', 'COMPANY_id', 'ldg_id'], columns: 3 },
      { id: 'employee', label: 'Employee Details (auto)', fields: ['assignment_id', 'employee_name', 'employee_number', 'department_id', 'designation_id'], columns: 2 },
      { id: 'amount', label: 'Loan Amount & Tenure', fields: ['request_date', 'requested_amount', 'eligible_loan_amount', 'requested_installments', 'eligibility_status'], columns: 2 },
      { id: 'terms', label: 'Recovery & Interest (auto from policy)', fields: ['recovery_type', 'interest_applicable', 'interest_type', 'interest_rate', 'emi_amount', 'first_deduction_period'], columns: 2 },
      { id: 'purpose', label: 'Purpose & Attachment', fields: ['purpose_of_loan', 'attachment_url'], columns: 1 },
      { id: 'approval', label: 'Approval Workflow', fields: ['approval_status', 'submitted_by', 'submitted_date', 'approved_by', 'approval_date', 'rejection_reason', 'rejected_by', 'rejected_date'], columns: 2 },
      { id: 'status', label: 'Outstanding & Status', fields: ['outstanding_amount', 'total_recovered', 'loan_status', 'notes'], columns: 2 },
      { id: 'period', label: 'Effective Period', fields: ['effective_from', 'effective_to'], columns: 2 },
    ],
    fields: {
      policy_id: {
        ...FK.advanceSalaryPolicy, required: true,
        autoFillFromFk: {
          loan_type: 'loan_type',
          recovery_type: 'recovery_type',
          interest_applicable: 'interest_applicable',
          interest_type: 'interest_type',
          interest_rate: 'interest_rate',
          eligible_loan_amount: 'max_loan_amount',
        },
        hint: 'Select policy first — loan_type, recovery_type, interest_*, eligible amount auto-fetched'
      },
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg },
      assignment_id: {
        ...FK.assignment, required: true,
        autoFillFromFk: {
          employee_name: 'employee_name',
          employee_number: 'employee_code',
          department_id: 'department_id',
          designation_id: 'designation_id',
        },
        hint: 'Employee details auto-filled below from selected assignment'
      },
      employee_name: {
        type: 'text', label: 'Employee Name (auto)', required: false, maxLength: 150,
        readOnly: true, hint: 'Auto-fetched from Person via Assignment'
      },
      employee_number: {
        type: 'text', label: 'Employee Number (auto)', required: false, maxLength: 30,
        readOnly: true
      },
      department_id: {
        ...FK.department, required: false, readOnly: true,
        hint: 'Auto-fetched from Assignment'
      },
      designation_id: {
        ...FK.designation, required: false, readOnly: true,
        hint: 'Auto-fetched from Assignment'
      },
      loan_type: {
        type: 'dropdown', label: 'ADVANCE SALARY TYPE (auto from policy)', required: true,
        source: 'ADVANCE_SALARY_TYPE', valueField: 'type_code', labelField: 'type_name',
        readOnly: true
      },
      request_date: {
        type: 'date', label: 'Request Date', required: true,
        hint: 'Defaults to today when creating new'
      },
      requested_amount: {
        type: 'number', label: 'Requested Amount (₹)', required: true,
        min: 1000, max: 99999999,
        hint: 'Amount employee is asking for'
      },
      eligible_loan_amount: {
        type: 'number', label: 'Eligible Loan Amount (₹)', required: false,
        min: 0, max: 99999999, readOnly: true,
        hint: 'Auto-calculated from policy + salary'
      },
      eligibility_status: {
        type: 'select', label: 'Eligibility Status', required: false,
        options: [
          { value: 'ELIGIBLE', label: 'Eligible' },
          { value: 'NOT_ELIGIBLE', label: 'Not Eligible' },
          { value: 'CONDITIONAL', label: 'Conditionally Eligible' },
        ],
        readOnly: true,
        recomputeOnChange: true,
        compute: (form) => {
          const req = Number(form.requested_amount) || 0;
          const eligible = Number(form.eligible_loan_amount) || 0;
          if (!req || !eligible) return '';
          return req <= eligible ? 'ELIGIBLE' : 'NOT_ELIGIBLE';
        },
        hint: 'Auto-derived from request vs eligible amount'
      },
      requested_installments: {
        type: 'number', label: 'Requested Installments', required: true,
        min: 1, max: 360, integerOnly: true,
        hint: 'EMI months requested (must be within policy min/max)'
      },
      recovery_type: {
        type: 'select', label: 'Recovery Type', required: false,
        options: [
          { value: 'EMI', label: 'EMI Based' },
          { value: 'FULL', label: 'Full Recovery' },
          { value: 'PERCENTAGE', label: '% of Salary' },
        ],
        readOnly: true, hint: 'Auto-fetched from policy'
      },
      interest_applicable: { type: 'flag', label: 'Interest Applicable', required: false, readOnly: true },
      interest_type: {
        type: 'select', label: 'Interest Type', required: false,
        options: [
          { value: 'FLAT', label: 'Flat Rate' },
          { value: 'REDUCING', label: 'Reducing Balance' },
        ],
        readOnly: true
      },
      interest_rate: {
        type: 'number', label: 'Interest Rate %', required: false,
        min: 0, max: 36, readOnly: true,
        hint: 'Auto-fetched from policy'
      },
      emi_amount: {
        type: 'number', label: 'EMI Amount (₹)', required: false,
        min: 0, max: 9999999, readOnly: true,
        recomputeOnChange: true,
        compute: (form) => {
          const P = Number(form.requested_amount) || 0;
          const N = Number(form.requested_installments) || 0;
          const ratePct = Number(form.interest_rate) || 0;
          if (!P || !N) return 0;
          if (!ratePct) return Math.round(P / N);
          const r = (ratePct / 100) / 12;
          const pow = Math.pow(1 + r, N);
          return Math.round(P * r * pow / (pow - 1));
        },
        hint: 'Auto-calculated from amount, rate, and installments'
      },
      first_deduction_period: {
        type: 'select', label: 'First Deduction Period', required: false,
        options: [
          { value: 'SAME_MONTH', label: 'Same Month' },
          { value: 'NEXT_PAYROLL', label: 'Next Payroll' },
          { value: 'NEXT_MONTH', label: 'Next Month' },
        ]
      },
      purpose_of_loan: {
        type: 'text', label: 'Purpose of Loan', required: true,
        minLength: 10, maxLength: 500,
        hint: 'Brief reason for the loan'
      },
      attachment_url: {
        type: 'url', label: 'Attachment URL', required: false, maxLength: 500,
        ...PATTERNS.URL,
        hint: 'Link to supporting document (salary slip, ID proof, etc.)'
      },
      approval_status: {
        type: 'select', label: 'Approval Status', required: true, readOnly: true,
        defaultValue: 'PENDING',
        options: [
          { value: 'DRAFT', label: 'Draft' },
          { value: 'PENDING', label: 'Pending Approval' },
          { value: 'APPROVED', label: 'Approved' },
          { value: 'REJECTED', label: 'Rejected' },
          { value: 'HOLD', label: 'Hold' },
          { value: 'WITHDRAWN', label: 'Withdrawn' },
        ]
      },
      submitted_by: {
        ...FK.person, required: false, label: 'Submitted By',
        hint: 'Usually the employee themselves'
      },
      submitted_date: { type: 'date', label: 'Submitted Date', required: false, readOnly: true },
      approved_by: {
        ...FK.person, required: false, label: 'Approved By', readOnly: true,
        hint: 'Manager / HR who approved'
      },
      approval_date: { type: 'date', label: 'Approval Date', required: false, readOnly: true },
      rejection_reason: {
        type: 'text', label: 'Rejection Reason', required: false, maxLength: 500,
        dynamicRequired: (formData) => formData.approval_status === 'REJECTED',
        showWhen: { field: 'approval_status', equals: 'REJECTED' },
        hint: 'Mandatory if Approval Status = Rejected'
      },
      rejected_by: {
        ...FK.person, label: 'Rejected By', required: false, readOnly: true,
        showWhen: { field: 'approval_status', equals: 'REJECTED' }
      },
      rejected_date: {
        type: 'date', label: 'Rejected Date', required: false, readOnly: true,
        showWhen: { field: 'approval_status', equals: 'REJECTED' }
      },
      payroll_deduction_element_id: {
        ...FK.element, required: false, label: 'Payroll Deduction Element',
        readOnly: true, hint: 'Auto-fetched from policy'
      },
      outstanding_amount: {
        type: 'number', label: 'Outstanding (₹)', required: false,
        min: 0, max: 99999999, readOnly: true,
        hint: 'Requested - recovered (updated by repayment engine)'
      },
      total_recovered: {
        type: 'number', label: 'Total Recovered (₹)', required: false,
        min: 0, max: 99999999, readOnly: true,
        hint: 'Sum of all EMI payments'
      },
      loan_status: {
        type: 'select', label: 'Loan Status', required: false,
        options: [
          { value: 'NEW', label: 'New' },
          { value: 'ACTIVE', label: 'Active' },
          { value: 'CLOSED', label: 'Closed' },
          { value: 'HOLD', label: 'Hold' },
        ]
      },
      notes: { type: 'text', label: 'Notes / Remarks', required: false, maxLength: 500 },
      ...DATES,
      ...AUTO,
    },
  },

  ADVANCE_SALARY: {
    pk: 'loan_id', pkPrefix: 'LN', pkPad: 5, label: 'Advance Salary', showPk: true,
    displayColumns: ['loan_id', 'assignment_id', 'loan_type', 'approved_amount', 'emi_amount', 'number_of_installments', 'outstanding', 'loan_status', 'rejection_reason'],
    displayJoins: {
      assignment_id: {
        source: 'ASSIGNMENT', valueField: 'assignment_id', labelCombo: ['assignment_id', 'employee_code'],
        chain: { via: 'person_id', source: 'PERSON', keyField: 'person_id', labelField: 'full_name' }
      }
    },
    formNotice: {
      severity: 'info',
      title: 'Active loan account',
      message: 'Auto-created when a Loan Request is approved. Tracks disbursement, EMI schedule, payments, and outstanding balance. Manual creation only for one-off legacy loans or corrections.',
    },
    sections: [
      { id: 'refs', label: 'References', fields: ['loan_request_id', 'policy_id', 'bg_id', 'COMPANY_id', 'ldg_id'], columns: 2 },
      { id: 'employee', label: 'Employee (auto)', fields: ['assignment_id', 'employee_name', 'employee_number', 'department_id', 'designation_id'], columns: 2 },
      { id: 'amount', label: 'Loan Amount', fields: ['loan_type', 'loan_amount', 'approved_amount', 'currency'], columns: 2 },
      { id: 'recovery', label: 'Recovery / EMI', fields: ['recovery_type', 'number_of_installments', 'tenure_months', 'emi_amount'], columns: 2 },
      { id: 'interest', label: 'Interest', fields: ['interest_applicable', 'interest_type', 'interest_rate', 'total_interest_amount', 'total_payable_amount'], columns: 2 },
      { id: 'recoveryDates', label: 'Recovery Dates', fields: ['first_period_id', 'recovery_start_date', 'recovery_completion_date', 'last_deduction_date'], columns: 2 },
      { id: 'balance', label: 'Balance Tracking', fields: ['total_recovered', 'outstanding'], columns: 2 },
      { id: 'payment', label: 'Disbursement Payment', fields: ['payment_method', 'bank_account_id', 'payment_date', 'transaction_reference', 'payment_status'], columns: 2 },
      { id: 'lifecycle', label: 'Status & Lifecycle', fields: ['disbursement_date', 'loan_status', 'approved_by', 'approval_date', 'loan_closure_date'], columns: 2 },
      { id: 'rejection', label: 'Rejection Details', fields: ['rejection_reason', 'rejected_date', 'rejected_by'], columns: 2 },
      { id: 'flags', label: 'Control Flags', fields: ['auto_deduction_flag', 'recovery_freeze', 'payroll_hold_flag'], columns: 3 },
      { id: 'docs', label: 'Documentation', fields: ['payroll_deduction_element_id', 'attachment_url', 'employee_remarks', 'hr_remarks', 'notes'], columns: 1 },
      { id: 'period', label: 'Effective Period', fields: ['effective_from', 'effective_to'], columns: 2 },
    ],
    fields: {
      loan_request_id: {
        ...FK.advanceSalaryRequest, required: true,
        autoFillFromFk: {
          bg_id: 'bg_id',
          COMPANY_id: 'COMPANY_id',
          ldg_id: 'ldg_id',
          assignment_id: 'assignment_id',
          employee_name: 'employee_name',
          employee_number: 'employee_number',
          department_id: 'department_id',
          designation_id: 'designation_id',
          policy_id: 'policy_id',
          loan_type: 'loan_type',
          loan_amount: 'requested_amount',
          approved_amount: 'eligible_loan_amount',
          number_of_installments: 'requested_installments',
          tenure_months: 'requested_installments',
          emi_amount: 'emi_amount',
          recovery_type: 'recovery_type',
          interest_applicable: 'interest_applicable',
          interest_type: 'interest_type',
          interest_rate: 'interest_rate',
          total_payable_amount: 'eligible_loan_amount',
          outstanding: 'eligible_loan_amount',
          first_period_id: 'first_deduction_period',
          request_date: 'request_date',
          approved_by: 'approved_by',
          approval_date: 'approval_date',
          rejection_reason: 'rejection_reason',
          rejected_by: 'rejected_by',
          rejected_date: 'rejected_date',
        },
        hint: 'Originating loan request — selecting it auto-fills all loan terms below'
      },
      policy_id: {
        ...FK.advanceSalaryPolicy, required: false, readOnly: true,
        hint: 'Policy this loan was approved under (auto from request)'
      },
      bg_id: {
        ...FK.bg, readOnly: true,
        cascade: ['COMPANY_id'],
        hint: 'Auto from loan request'
      },
      COMPANY_id: {
        ...FK.company, readOnly: true,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Auto from loan request'
      },
      ldg_id: { ...FK.ldg, readOnly: true, hint: 'Auto from loan request' },
      assignment_id: {
        ...FK.assignment, required: true, readOnly: true,
        autoFillFromFk: {
          employee_name: 'employee_name',
          employee_number: 'employee_code',
          department_id: 'department_id',
          designation_id: 'designation_id',
        },
        hint: 'Employee details auto-filled from this'
      },
      request_date: {
        type: 'date', label: 'Request Date', required: false, readOnly: true, hidden: true,
        hint: 'Auto-filled from loan request'
      },
      employee_name: { type: 'text', label: 'Employee Name (auto)', required: false, maxLength: 150, readOnly: true },
      employee_number: { type: 'text', label: 'Employee Number (auto)', required: false, maxLength: 30, readOnly: true },
      department_id: { ...FK.department, required: false, readOnly: true },
      designation_id: { ...FK.designation, required: false, readOnly: true },
      loan_type: {
        type: 'dropdown', label: 'ADVANCE SALARY TYPE', required: true, readOnly: true,
        source: 'ADVANCE_SALARY_TYPE', valueField: 'type_code', labelField: 'type_name',
        hint: 'Auto from loan request'
      },
      loan_amount: {
        type: 'number', label: 'Requested Amount (₹)', required: true, readOnly: true,
        min: 1000, max: 99999999,
        hint: 'Auto from loan request'
      },
      approved_amount: {
        type: 'number', label: 'Approved Amount (₹)', required: true,
        min: 1000, max: 99999999,
        maxRefField: 'loan_amount',
        maxRefFieldMsg: 'Approved Amount cannot be greater than Requested Amount.',
        hint: 'Cannot exceed requested amount'
      },
      currency: {
        ...FK.currency, required: true, readOnly: true,
        defaultValue: 'INR',
        hint: 'Defaults to INR'
      },
      recovery_type: {
        type: 'select', label: 'Recovery Type', required: true, readOnly: true,
        options: [
          { value: 'EMI', label: 'EMI Based' },
          { value: 'FULL', label: 'Full Recovery (Lump Sum)' },
          { value: 'PERCENTAGE', label: '% of Salary' },
        ],
        hint: 'Auto from loan request'
      },
      number_of_installments: {
        type: 'number', label: 'Number of Installments', required: true, readOnly: true,
        min: 1, max: 360, integerOnly: true,
        hint: 'Auto from loan request'
      },
      tenure_months: {
        type: 'number', label: 'Tenure (Months)', required: false,
        min: 1, max: 360, integerOnly: true,
        hint: 'Calendar months (may differ from installments if seasonal)'
      },
      emi_amount: {
        type: 'number', label: 'EMI Amount (₹)', required: true, readOnly: true,
        min: 0, max: 9999999,
        hint: 'Auto from loan request'
      },
      interest_applicable: { type: 'flag', label: 'Interest Applicable', required: true },
      interest_type: {
        type: 'select', label: 'Interest Type', required: false, readOnly: true,
        options: [
          { value: 'FLAT', label: 'Flat Rate' },
          { value: 'REDUCING', label: 'Reducing Balance' },
        ],
        hint: 'Auto from loan request'
      },
      interest_rate: {
        type: 'number', label: 'Interest Rate %', required: false, readOnly: true,
        min: 0, max: 36,
        hint: 'Auto from loan request'
      },
      total_interest_amount: {
        type: 'number', label: 'Total Interest (₹)', required: false,
        min: 0, max: 9999999, readOnly: true,
        recomputeOnChange: true,
        compute: (form) => {
          const emi = Number(form.emi_amount) || 0;
          const n = Number(form.number_of_installments) || 0;
          const principal = Number(form.approved_amount) || Number(form.loan_amount) || 0;
          if (!emi || !n) return 0;
          return Math.max(0, Math.round(emi * n - principal));
        },
        hint: 'Auto: (EMI × installments) − approved amount'
      },
      total_payable_amount: {
        type: 'number', label: 'Total Payable (₹)', required: false,
        min: 0, max: 99999999, readOnly: true,
        recomputeOnChange: true,
        compute: (form) => {
          const emi = Number(form.emi_amount) || 0;
          const n = Number(form.number_of_installments) || 0;
          if (!emi || !n) return 0;
          return Math.round(emi * n);
        },
        hint: 'Auto: EMI × installments'
      },
      first_period_id: {
        ...FK.period, required: true, labelCombo: ['period_name', 'period_type_display'], label: 'First Recovery Period',
        hint: 'Pay period when first EMI deducts'
      },
      recovery_start_date: {
        type: 'date', label: 'Recovery Start Date', required: false,
        afterField: 'request_date',
        afterFieldMsg: 'Date cannot be earlier than the Advance Salary Request Date.',
        hint: 'First EMI date (auto from First Period)'
      },
      recovery_completion_date: {
        type: 'date', label: 'Recovery Completion Date', required: false, readOnly: false,
        afterField: 'request_date',
        afterFieldMsg: 'Date cannot be earlier than the Advance Salary Request Date.',
        hint: 'Auto-calculated: start + installments months'
      },
      last_deduction_date: {
        type: 'date', label: 'Last Deduction Date', required: false, readOnly: false,
        afterField: 'request_date',
        afterFieldMsg: 'Date cannot be earlier than the Advance Salary Request Date.',
        hint: 'Updated after each payroll run'
      },
      total_recovered: {
        type: 'number', label: 'Total Recovered (₹)', required: false,
        min: 0, max: 99999999, readOnly: true
      },
      outstanding: {
        type: 'number', label: 'Outstanding (₹)', required: false,
        min: 0, max: 99999999, readOnly: true,
        hint: 'Auto-updated: payable - recovered'
      },
      payroll_deduction_element_id: { ...FK.element, required: false, label: 'Payroll Deduction Element' },
      payment_method: {
        type: 'select', label: 'Disbursement Method', required: false,
        options: [
          { value: 'BANK_TRANSFER', label: 'Bank Transfer (NEFT/RTGS)' },
          { value: 'IMPS', label: 'IMPS' },
          { value: 'UPI', label: 'UPI' },
          { value: 'CASH', label: 'Cash' },
          { value: 'CHEQUE', label: 'Cheque' },
          { value: 'DD', label: 'Demand Draft' },
        ]
      },
      bank_account_id: {
        ...FK.personalPaymentMethod, label: 'Bank Account', required: true,
        dependsOn: 'assignment_id', filterBy: 'assignment_id',
        autoSelectSingle: true,
        emptyMsg: 'No active bank account found for this employee. Please create a Bank Account first.',
        hint: 'Employee account where loan disbursed'
      },
      payment_date: { type: 'date', label: 'Payment Date', required: false },
      transaction_reference: {
        type: 'text', label: 'Transaction Reference', required: false, maxLength: 50,
        pattern: '^[A-Z0-9\\-/]{6,50}$|^$',
        patternMsg: 'UTR or bank reference: 6-50 alphanumeric chars',
        autoUppercase: true
      },
      payment_status: {
        type: 'select', label: 'Disbursement Status', required: false,
        options: [
          { value: 'PENDING', label: 'Pending' },
          { value: 'INITIATED', label: 'Initiated' },
          { value: 'PAID', label: 'Paid' },
          { value: 'FAILED', label: 'Failed' },
          { value: 'REVERSED', label: 'Reversed' },
        ]
      },
      disbursement_date: {
        type: 'date', label: 'Disbursement Date', required: false,
        hint: 'Date loan was paid out'
      },
      loan_status: {
        type: 'select', label: 'Loan Status', required: true,
        options: [
          { value: 'PENDING', label: 'Pending Approval' },
          { value: 'APPROVED', label: 'Approved' },
          { value: 'DISBURSED', label: 'Disbursed' },
          { value: 'ACTIVE', label: 'Active (Recovering)' },
          { value: 'CLOSED', label: 'Closed' },
          { value: 'DEFAULTED', label: 'Defaulted' },
          { value: 'REJECTED', label: 'Rejected' },
          { value: 'HOLD', label: 'On Hold' },
        ]
      },
      approved_by: { ...FK.person, required: false, label: 'Approved By' },
      approval_date: { type: 'date', label: 'Approval Date', required: false },
      loan_closure_date: {
        type: 'date', label: 'Loan Closure Date', required: false,
        afterField: 'approval_date',
        hint: 'Editable — set when loan is closed'
      },
      rejection_reason: {
        type: 'text', label: 'Rejection Reason', required: false, maxLength: 500,
        dynamicRequired: (formData) => formData.loan_status === 'REJECTED',
        showWhen: { field: 'loan_status', equals: 'REJECTED' }
      },
      rejected_by: {
        ...FK.person, label: 'Rejected By', required: false,
        dynamicRequired: (formData) => formData.loan_status === 'REJECTED',
        showWhen: { field: 'loan_status', equals: 'REJECTED' }
      },
      rejected_date: {
        type: 'date', label: 'Rejected Date', required: false, readOnly: true,
        showWhen: { field: 'loan_status', equals: 'REJECTED' }
      },
      auto_deduction_flag: {
        type: 'flag', label: 'Auto Payroll Deduction', required: true,
        hint: 'Y = engine deducts EMI each payroll run'
      },
      recovery_freeze: {
        type: 'flag', label: 'Recovery Freeze', required: true,
        hint: 'Y = temporarily stop deductions (e.g. extended leave)'
      },
      payroll_hold_flag: {
        type: 'flag', label: 'Payroll Hold', required: true,
        hint: 'Y = full hold on all deductions'
      },
      attachment_url: {
        type: 'url', label: 'Attachment URL', required: false, maxLength: 500,
        ...PATTERNS.URL
      },
      employee_remarks: { type: 'text', label: 'Employee Remarks', required: false, maxLength: 500 },
      hr_remarks: { type: 'text', label: 'HR Remarks', required: false, maxLength: 500 },
      notes: { type: 'text', label: 'Notes', required: false, maxLength: 500 },
      ...DATES,
      ...AUTO,
    },
  },

  ADVANCE_SALARY_REPAYMENT: {
    pk: 'repayment_id', pkPrefix: 'LRP', pkPad: 5, label: 'Advance Salary Repayment', showPk: true,
    displayColumns: ['repayment_id', 'loan_id', 'assignment_id', 'installment_number', 'emi_amount', 'principal_amount_monthly', 'cash_amount', 'payroll_amount', 'recovery_type', 'outstanding_balance', 'recovery_status'],
    displayJoins: {
      assignment_id: {
        source: 'ASSIGNMENT', valueField: 'assignment_id', labelCombo: ['assignment_id', 'employee_code'],
        chain: { via: 'person_id', source: 'PERSON', keyField: 'person_id', labelField: 'full_name' }
      }
    },
    formNotice: {
      severity: 'info',
      title: 'Monthly EMI ledger',
      message: 'Auto-generated each payroll run for active loans. Splits EMI into principal + interest, tracks outstanding balance. Manual entry only for legacy data import or manual corrections.',
    },
    sections: [
      { id: 'refs', label: 'References', fields: ['loan_id', 'loan_request_id', 'bg_id', 'COMPANY_id', 'ldg_id'], columns: 2 },
      { id: 'employee', label: 'Employee Snapshot', fields: ['assignment_id', 'employee_name', 'employee_number'], columns: 2 },
      { id: 'installment', label: 'Installment', fields: ['installment_number', 'total_installments', 'emi_amount', 'loan_amount'], columns: 2 },
      { id: 'breakdown', label: 'Monthly Breakdown', fields: ['principal_amount_monthly', 'interest_amount_monthly', 'total_deduction_amount', 'cash_amount', 'payroll_amount'], columns: 3 },
      { id: 'period', label: 'Payroll Period', fields: ['period_id', 'deduction_period', 'deduction_date', 'payroll_run_id'], columns: 2 },
      { id: 'balance', label: 'Balance Tracking', fields: ['outstanding_balance', 'total_recovered', 'balance_after'], columns: 3 },
      { id: 'status', label: 'Recovery Status', fields: ['recovery_status', 'recovery_type', 'currency'], columns: 3 },
      { id: 'payment', label: 'Payment Details', fields: ['payment_mode', 'transaction_reference', 'bank_account_id'], columns: 2 },
      { id: 'control', label: 'Control', fields: ['freeze_flag', 'notes'], columns: 2 },
      { id: 'other', label: 'Other Details', fields: ['emi_number', 'principal', 'interest', 'entry_id'], columns: 2 },
      { id: 'periodEff', label: 'Effective Period', fields: ['status', 'effective_from', 'effective_to'], columns: 2 },
    ],
    fields: {
      loan_id: {
        ...FK.advanceSalary, required: true,
        autoFillFromFk: {
          loan_request_id: 'loan_request_id',
          assignment_id: 'assignment_id',
          employee_name: 'employee_name',
          employee_number: 'employee_number',
          bg_id: 'bg_id',
          COMPANY_id: 'COMPANY_id',
          ldg_id: 'ldg_id',
          loan_amount: 'approved_amount',
          emi_amount: 'emi_amount',
          total_installments: 'number_of_installments',
          recovery_type: 'recovery_type',
          currency: 'currency',
          principal_amount_monthly: 'emi_amount',
          total_deduction_amount: 'emi_amount',
          principal: 'emi_amount',
          interest_amount_monthly: 'total_interest_amount',
          interest: 'total_interest_amount',
          payment_mode: 'payment_method',
          bank_account_id: 'bank_account_id',
        },
        hint: 'Employee + amount + EMI auto-filled from selected loan'
      },
      loan_request_id: {
        ...FK.advanceSalaryRequest, required: false, readOnly: true,
        hint: 'Auto-fetched from loan'
      },
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg },
      assignment_id: {
        ...FK.assignment, required: true, readOnly: true,
        hint: 'Auto-fetched from loan'
      },
      employee_name: { type: 'text', label: 'Employee Name', required: false, maxLength: 150, readOnly: true },
      employee_number: { type: 'text', label: 'Employee Number', required: false, maxLength: 30, readOnly: true },
      loan_amount: {
        type: 'number', label: 'Original Loan Amount (₹)', required: false,
        min: 0, max: 99999999, readOnly: true
      },
      emi_amount: {
        type: 'number', label: 'EMI Amount (₹)', required: true,
        min: 0, max: 9999999, readOnly: true,
        hint: 'Standard monthly deduction'
      },
      installment_number: {
        type: 'number', label: 'Installment #', required: true,
        min: 1, max: 999, integerOnly: true,
        hint: 'Sequence: 1, 2, 3...'
      },
      total_installments: {
        type: 'number', label: 'Total Installments', required: false,
        min: 1, max: 360, integerOnly: true, readOnly: true,
        hint: 'Auto from loan tenure'
      },
      principal_amount_monthly: {
        type: 'number', label: 'Principal Portion (₹)', required: true,
        min: 0, max: 9999999,
        hint: 'Portion of EMI that reduces principal'
      },
      interest_amount_monthly: {
        type: 'number', label: 'Interest Portion (₹)', required: false,
        min: 0, max: 999999,
        hint: 'Portion of EMI that pays interest'
      },
      total_deduction_amount: {
        type: 'number', label: 'Total Deduction (₹)', required: true,
        min: 0, max: 9999999,
        hint: 'Principal + interest for this month'
      },
      cash_amount: {
        type: 'number', label: 'Cash Recovery Amount (₹)', required: false,
        min: 0, max: 9999999,
        hint: 'Direct cash / cheque recovery portion'
      },
      payroll_amount: {
        type: 'number', label: 'Payroll Recovery Amount (₹)', required: false,
        min: 0, max: 9999999,
        hint: 'Payroll deduction portion'
      },
      period_id: { ...FK.period, required: false },
      deduction_period: {
        type: 'text', label: 'Deduction Period (MMM-YYYY)', required: false, maxLength: 20,
        pattern: '^[A-Z]{3}-[0-9]{4}$|^$',
        patternMsg: 'Format: MMM-YYYY (e.g. FEB-2026)',
        autoUppercase: true
      },
      deduction_date: { type: 'date', label: 'Deduction Date', required: false },
      payroll_run_id: {
        ...FK.payrollRun, required: false,
        hint: 'Linked payroll execution that processed this EMI'
      },
      outstanding_balance: {
        type: 'number', label: 'Outstanding Balance After (₹)', required: false,
        min: 0, max: 99999999, readOnly: true,
        hint: 'Loan balance remaining after this EMI'
      },
      total_recovered: {
        type: 'number', label: 'Cumulative Recovered (₹)', required: false,
        min: 0, max: 99999999, readOnly: true,
        hint: 'Sum of all EMIs paid till date'
      },
      balance_after: {
        type: 'number', label: 'Balance After (legacy)', required: false,
        min: 0, max: 99999999, readOnly: true,
        hint: 'Legacy field — same as Outstanding Balance'
      },
      recovery_status: {
        type: 'select', label: 'Recovery Status', required: true,
        options: [
          { value: 'PENDING', label: 'Pending' },
          { value: 'PAID', label: 'Paid' },
          { value: 'PARTIALLY_PAID', label: 'Partially Paid' },
          { value: 'FAILED', label: 'Failed' },
          { value: 'SKIPPED', label: 'Skipped (LOP)' },
          { value: 'WAIVED', label: 'Waived' },
          { value: 'COMPLETED', label: 'Completed (Final EMI)' },
        ]
      },
      recovery_type: {
        type: 'select', label: 'Recovery Type', required: false,
        options: [
          { value: 'EMI', label: 'EMI' },
          { value: 'FULL', label: 'Full Recovery' },
          { value: 'PERCENTAGE', label: '% of Salary' },
        ],
        readOnly: true, hint: 'Auto from loan'
      },
      currency: {
        ...FK.currency, required: false, readOnly: true,
        hint: 'Auto from loan'
      },
      payment_mode: {
        type: 'select', label: 'Payment Mode', required: false,
        options: [
          { value: 'SALARY_DEDUCTION', label: 'Salary Deduction' },
          { value: 'MANUAL_PAYMENT', label: 'Manual Payment' },
          { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
          { value: 'CASH', label: 'Cash' },
          { value: 'CHEQUE', label: 'Cheque' },
        ]
      },
      transaction_reference: {
        type: 'text', label: 'Transaction Reference', required: false, maxLength: 50,
        pattern: '^[A-Z0-9\\-/]{4,50}$|^$',
        patternMsg: 'Bank reference: 4-50 alphanumeric',
        autoUppercase: true,
        hint: 'For manual payments outside payroll'
      },
      bank_account_id: {
        type: 'text', label: 'Bank Account', required: false, maxLength: 30,
        pattern: '^[0-9]{9,18}$|^$',
        patternMsg: '9-18 digit account number'
      },
      freeze_flag: {
        type: 'flag', label: 'Freeze Flag', required: true,
        hint: 'Y = skip this EMI (e.g. employee on LOP)'
      },
      notes: { type: 'text', label: 'Notes', required: false, maxLength: 500 },
      emi_number: { type: 'number', label: 'Emi Number', required: false },
      principal: { type: 'number', label: 'Principal', required: false },
      interest: { type: 'number', label: 'Interest', required: false },
      entry_id: { type: 'text', label: 'Entry Id', required: false },
      status: {
        type: 'flag', label: 'Status', required: true,
        hint: 'Toggle active status'
      },
      ...DATES,
      ...AUTO,
    },
  },

  // ══ PAYROLL RUN ══════════════════════════════════════════════════════════════

  PAYROLL_RUN: {
    pk: 'run_id', pkPrefix: 'RUN', pkPad: 5, label: 'Payroll Run',
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg },
      payroll_id: { ...FK.payroll },
      period_id: { ...FK.period },
      consolidation_group_id: { ...FK.consolidation },
      run_type: { type: 'text', label: 'Run Type', required: true, maxLength: 20 },
      run_name: { type: 'text', label: 'Run Name', required: true, maxLength: 100 },
      run_date: { type: 'date', label: 'Run Date', required: true },
      run_status: { type: 'text', label: 'Run Status', required: true, maxLength: 20 },
      ...DATES,
      ...AUTO,
    },
  },

  RUN_RESULT: {
    pk: 'result_id', pkPrefix: 'RES', pkPad: 5, label: 'Run Result',
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg },
      payroll_id: { ...FK.payroll },
      run_id: { ...FK.payrollRun },
      assignment_id: { ...FK.assignment },
      element_id: { ...FK.element },
      element_code: { type: 'text', label: 'Element Code', required: true, maxLength: 20 },
      classification: { type: 'text', label: 'Classification', required: true, maxLength: 50 },
      amount: { type: 'number', label: 'Amount', required: true, min: 0 },
      currency: { type: 'text', label: 'Currency', required: true, maxLength: 5 },
      ...DATES,
      ...AUTO,
    },
  },

  RUN_RESULT_VALUE: {
    pk: 'result_value_id', pkPrefix: 'RV', pkPad: 5, label: 'Run Result Value',
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg },
      payroll_id: { ...FK.payroll },
      result_value: { type: 'text', label: 'Result Value', required: true, maxLength: 100 },
      ...DATES,
      ...AUTO,
    },
  },

  // ══ BALANCES ═════════════════════════════════════════════════════════════════

  BALANCE_CATEGORY: {
    pk: 'category_id', pkPrefix: 'BC', pkPad: 3, label: 'Balance Category', showPk: true,
    displayColumns: ['category_id', 'category_code', 'category_name', 'status', 'effective_from'],
    sections: [
      { id: 'identity', label: 'Category Details', fields: ['category_code', 'category_name', 'description', 'display_sequence'] },
      { id: 'meta', label: 'Status & Period', fields: ['status', 'effective_from', 'effective_to'], columns: 2 }
    ],
    fields: {
      category_code: { type: 'text', label: 'Category Code', required: true, maxLength: 30, ...PATTERNS.ALPHA, autoUppercase: true },
      category_name: { type: 'text', label: 'Category Name', required: true, maxLength: 60, ...PATTERNS.NAME },
      description: { type: 'text', label: 'Description', required: false, maxLength: 200 },
      display_sequence: { type: 'number', label: 'Display Sequence', required: false, min: 1, max: 999, integerOnly: true },
      status: { type: 'flag', label: 'Status', required: true },
      ...DATES,
    }
  },

  BALANCE_DEFINITION: {
    pk: 'balance_def_id', pkPrefix: 'BD', pkPad: 5, label: 'Balance Definition',
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg },
      balance_name: { type: 'text', label: 'Balance Name', required: true, maxLength: 100 },
      reporting_name: { type: 'text', label: 'Reporting Name', required: false, maxLength: 100 },
      balance_category: { type: 'text', label: 'Balance Category', required: true, maxLength: 50, ...PATTERNS.ALPHANUMERIC_NO_SPECIAL },
      unit_of_measure: { type: 'text', label: 'Unit of Measure', required: true, maxLength: 20 },
      base_balance_id: { type: 'dropdown', label: 'Base Balance Id', required: false, source: 'BALANCE_DEFINITION', valueField: 'balance_def_id', labelField: 'balance_name' },
      remuneration_flag: { type: 'flag', label: 'Remuneration', required: true },
      ...STATUS,
      ...DATES,
      ...AUTO,
    },
  },

  DEFINED_BALANCE: {
    pk: 'defined_balance_id', pkPrefix: 'DBL', pkPad: 5, label: 'Defined Balance',
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg },
      balance_def_id: { ...FK.balanceDef },
      dimension_name: { type: 'text', label: 'Dimension Name', required: true, maxLength: 50 },
      dimension_type: { type: 'text', label: 'Dimension Type', required: true, maxLength: 30 },
      employment_level: { type: 'text', label: 'Employment Level', required: false, maxLength: 30 },
      context_type: { type: 'text', label: 'Context Type', required: false, maxLength: 30 },
      db_item_suffix: { type: 'text', label: 'DB Item Suffix', required: false, maxLength: 20 },
      ...STATUS,
      ...DATES,
      ...AUTO,
    },
  },

  BALANCE_VALUE: {
    pk: 'balance_id', pkPrefix: 'BV', pkPad: 5, label: 'Balance Value',
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg },
      defined_balance_id: { ...FK.definedBal },
      assignment_id: { ...FK.assignment },
      run_id: { ...FK.payrollRun, required: false },
      fiscal_year: { type: 'text', label: 'Fiscal Year', required: true, maxLength: 10 },
      balance_amount: { type: 'number', label: 'Balance Amount', required: true, min: 0 },
      currency: { type: 'text', label: 'Currency', required: true, maxLength: 5 },
      ...DATES,
      ...AUTO,
    },
  },


  // ══ PAYMENTS ═════════════════════════════════════════════════════════════

  PERSONAL_PAYMENT_METHOD: {
    pk: 'ppm_id', pkPrefix: 'PPM', pkPad: 5, label: 'Personal Payment Method',
    sections: [
      { id: 'org', label: 'References', fields: ['bg_id', 'COMPANY_id', 'ldg_id', 'assignment_id', 'person_id', 'prel_id'], columns: 2 },
      { id: 'details', label: 'Bank Details', fields: ['payment_type', 'bank_id', 'bank_account_id', 'ifsc_code', 'account_type', 'customer_id'], columns: 2 },
      { id: 'payment_config', label: 'Payment Configuration', fields: ['is_primary', 'priority', 'percentage', 'fixed_amount', 'currency'], columns: 2 },
      { id: 'metadata', label: 'Status & Dates', fields: ['status', 'active_flag', 'effective_from', 'effective_to'], columns: 2 },
    ],
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg },
      assignment_id: {
        ...FK.assignment,
        autoFillFromFk: {
          COMPANY_id: 'COMPANY_id',
          bg_id: 'bg_id',
          ldg_id: 'ldg_id',
          person_id: 'person_id',
        },
        hint: 'Selecting assignment auto-fills org context, Person and Payroll Relationship'
      },
      person_id: {
        ...FK.person,
        readOnly: true,
        hint: 'Auto-fetched from Assignment'
      },
      prel_id: {
        type: 'dropdown', label: 'Payroll Relationship', required: false,
        source: 'PAYROLL_RELATIONSHIP', valueField: 'prel_id', labelField: 'prel_id',
        labelCombo: ['prel_id'],
        readOnly: true,
        hint: "Auto-fetched from Assignment's person"
      },
      payment_type: {
        type: 'select', label: 'Payment Type', required: true,
        options: [
          { value: 'BANK_TRANSFER', label: 'Bank Transfer (NEFT/RTGS)' },
          { value: 'IMPS', label: 'IMPS' },
          { value: 'UPI', label: 'UPI' },
          { value: 'CASH', label: 'Cash' },
          { value: 'CHEQUE', label: 'Cheque' },
          { value: 'DD', label: 'Demand Draft' },
          { value: 'WALLET', label: 'Wallet (Paytm/etc.)' },
        ]
      },
      bank_id: {
        ...FK.bank, required: false,
        hint: 'Required for Bank Transfer / IMPS / Cheque'
      },
      bank_account_id: {
        type: 'text', label: 'Account Number', required: true,
        hint: 'Enter your bank account number (Must be globally unique)'
      },
      ifsc_code: {
        type: 'text', label: 'IFSC Code', required: false,
        hint: 'Required for Bank Transfer / IMPS'
      },
      account_type: {
        type: 'select', label: 'Account Type', required: false,
        options: [
          { value: 'Savings', label: 'Savings' },
          { value: 'Current', label: 'Current' },
          { value: 'Salary', label: 'Salary' }
        ]
      },
      customer_id: {
        type: 'text', label: 'Customer ID', required: true,
        maxLength: 20,
        hint: 'Bank customer ID (6-20 alphanumeric characters)',
        validate: v => {
          if (!v) return 'Customer ID is required';
          if (!/^[A-Za-z0-9]{6,20}$/.test(v)) return 'Customer ID must be 6-20 alphanumeric characters';
          return null;
        }
      },
      is_primary: {
        type: 'select', label: 'Primary Account?', required: true,
        options: [
          { value: 'Y', label: 'Yes' },
          { value: 'N', label: 'No' }
        ],
        hint: 'Only one account can be primary'
      },
      status: {
        type: 'select', label: 'Account Status', required: true,
        options: [
          { value: 'Active', label: 'Active' },
          { value: 'Inactive', label: 'Inactive' },
          { value: 'Closed', label: 'Closed' }
        ],
        hint: 'An employee can only have one Active account per bank'
      },
      priority: {
        type: 'number', label: 'Priority', required: true, min: 1, max: 99, integerOnly: true,
        hint: 'Order of payment when split (1 = highest priority)'
      },
      percentage: {
        type: 'number', label: 'Percentage', required: false, min: 0, max: 100,
        hint: 'Percentage of net pay to send (max 100)'
      },
      fixed_amount: {
        type: 'number', label: 'Fixed Amount (₹)', required: false, min: 0, max: 9999999,
        hint: 'Use either percentage OR fixed amount, not both'
      },
      currency: { ...FK.currency, required: true },
      ...STATUS,
      ...DATES,
      ...AUTO,
    },
  },
  PAYMENT: {
    pk: 'payment_id', pkPrefix: 'PAY', pkPad: 5, label: 'Payment',
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg },
      run_id: { ...FK.payrollRun },
      assignment_id: {
        ...FK.assignment,
        autoFillFromFk: {
          COMPANY_id: 'COMPANY_id',
          bg_id: 'bg_id',
          ldg_id: 'ldg_id',
        },
        hint: 'Selecting assignment auto-fills org context (Company/BG/LDG)'
      },
      gross_amount: { type: 'number', label: 'Gross Amount (₹)', required: true, min: 0, max: 99999999 },
      total_deductions: { type: 'number', label: 'Total Deductions (₹)', required: false, min: 0, max: 99999999 },
      net_amount: { type: 'number', label: 'Net Amount (₹)', required: true, min: 0, max: 99999999 },
      currency: { ...FK.currency, required: true },
      payment_method: {
        type: 'select', label: 'Payment Method', required: true,
        options: [
          { value: 'NEFT', label: 'NEFT' },
          { value: 'RTGS', label: 'RTGS' },
          { value: 'IMPS', label: 'IMPS' },
          { value: 'UPI', label: 'UPI' },
          { value: 'CASH', label: 'Cash' },
          { value: 'CHEQUE', label: 'Cheque' },
          { value: 'DD', label: 'Demand Draft' },
        ]
      },
      payment_status: {
        type: 'select', label: 'Payment Status', required: true,
        options: [
          { value: 'PENDING', label: 'Pending' },
          { value: 'INITIATED', label: 'Initiated' },
          { value: 'PROCESSING', label: 'Processing' },
          { value: 'PAID', label: 'Paid' },
          { value: 'FAILED', label: 'Failed' },
          { value: 'REVERSED', label: 'Reversed' },
          { value: 'CANCELLED', label: 'Cancelled' },
        ]
      },
      payment_date: { type: 'date', label: 'Payment Date', required: true },
      ...DATES,
      ...AUTO,
    },
  },

  PAYSLIP: {
    pk: 'payslip_id', pkPrefix: 'PS', pkPad: 5, label: 'Payslip',
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg },
      run_id: { ...FK.payrollRun },
      assignment_id: {
        ...FK.assignment,
        autoFillFromFk: {
          COMPANY_id: 'COMPANY_id',
          bg_id: 'bg_id',
          ldg_id: 'ldg_id',
        },
        hint: 'Selecting assignment auto-fills org context (Company/BG/LDG)'
      },
      period_name: {
        type: 'text', label: 'Period Name', required: true, maxLength: 100
      },
      working_days: { type: 'number', label: 'Working Days', required: true, min: 0, max: 31, integerOnly: true },
      gross_earnings: { type: 'number', label: 'Gross Earnings (₹)', required: true, min: 0, max: 9999999 },
      total_deductions: { type: 'number', label: 'Total Deductions (₹)', required: false, min: 0, max: 9999999 },
      net_payable: { type: 'number', label: 'Net Payable (₹)', required: true, min: 0, max: 9999999 },
      currency: { ...FK.currency, required: true },
      status: {
        type: 'select', label: 'Status', required: true,
        options: [
          { value: 'DRAFT', label: 'Draft' },
          { value: 'GENERATED', label: 'Generated' },
          { value: 'APPROVED', label: 'Approved' },
          { value: 'PUBLISHED', label: 'Published to Employee' },
          { value: 'CORRECTED', label: 'Corrected' },
          { value: 'CANCELLED', label: 'Cancelled' },
        ]
      },
      email_sent_flag: { type: 'flag', label: 'Email Sent', required: true },
      ...DATES,
      ...AUTO,
    },
  },

  BANK_PAYMENT_FILE: {
    pk: 'file_id', pkPrefix: 'BPF', pkPad: 5, label: 'PAYSLIP DATA', showPk: true,
    displayColumns: ['file_id', 'run_id', 'file_name', 'file_type', 'total_records', 'total_amount', 'currency', 'bank_id', 'file_status', 'file_generation_date'],
    formNotice: {
      severity: 'info',
      title: 'System-generated artifact',
      message: 'Bank Payment File records are typically auto-generated by the Payroll Run engine after approval. Manual entry should only be used for testing or correcting status / acknowledgement fields. Batch totals must match the underlying Payment records.',
    },
    sections: [
      { id: 'references', label: 'References', fields: ['bg_id', 'COMPANY_id', 'ldg_id', 'run_id', 'bank_id'], columns: 2 },
      { id: 'identity', label: 'File Identity', fields: ['file_name', 'file_type', 'file_generation_date', 'file_path'], columns: 2 },
      { id: 'batch', label: 'Batch Totals', fields: ['total_records', 'total_amount', 'currency'], columns: 2 },
      { id: 'banking', label: 'Banking Details', fields: ['sender_account'], columns: 2 },
      { id: 'status', label: 'Status & Acknowledgement', fields: ['file_status', 'sent_to_bank_date', 'bank_ack_ref'], columns: 2 },
      { id: 'effective', label: 'Effective Period', fields: ['effective_from', 'effective_to'], columns: 2 },
    ],
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg },
      run_id: {
        ...FK.payrollRun,
        hint: 'Payroll Run this file was generated from'
      },
      bank_id: {
        ...FK.bank, required: true,
        hint: 'Sender bank — usually company\'s payroll account bank'
      },
      file_name: {
        type: 'text', label: 'File Name', required: true,
        maxLength: 100,
        pattern: '^[A-Za-z0-9_\\-\\.]+\\.(txt|csv|xml|xls|xlsx)$',
        patternMsg: 'Letters, digits, underscore, hyphen, dot only — must end in .txt/.csv/.xml/.xls/.xlsx',
        hint: 'e.g. NEFT_FEB2026_VVSPL.txt'
      },
      file_type: {
        type: 'select', label: 'File Type', required: true,
        options: [
          { value: 'NEFT_FLAT', label: 'NEFT Flat File' },
          { value: 'RTGS_FLAT', label: 'RTGS Flat File' },
          { value: 'IMPS_BATCH', label: 'IMPS Batch' },
          { value: 'ACH_CREDIT', label: 'ACH Credit' },
          { value: 'CHEQUE_BATCH', label: 'Cheque Batch' },
          { value: 'HDFC_NETBANK', label: 'HDFC NetBanking' },
          { value: 'ICICI_CIB', label: 'ICICI CIB' },
          { value: 'SBI_CINB', label: 'SBI CINB' },
        ],
        hint: 'Bank-specific format used to upload to corporate banking portal'
      },
      file_generation_date: {
        type: 'date', label: 'Generated On', required: true,
        hint: 'Date file was generated by the payroll engine'
      },
      file_path: {
        type: 'text', label: 'File Path / URL', required: false,
        maxLength: 255,
        hint: 'Server path or cloud storage URL where file is stored (e.g. /payroll/bank-files/2026-02/...)'
      },
      total_records: {
        type: 'number', label: 'Total Records', required: true,
        min: 1, max: 999999, integerOnly: true,
        hint: 'Must equal count of payment lines in this file'
      },
      total_amount: {
        type: 'number', label: 'Total Amount (₹)', required: true,
        min: 0.01, max: 9999999999,
        hint: 'Sum of all payment amounts — must match payment records exactly'
      },
      currency: {
        ...FK.currency, required: true,
        hint: 'Usually INR for Indian payroll'
      },
      sender_account: {
        type: 'text', label: 'Sender Account Number', required: true,
        maxLength: 18, minLength: 9,
        pattern: '^[0-9]{9,18}$',
        patternMsg: 'Account number must be 9-18 digits',
        hint: 'Company\'s payroll disbursement account'
      },
      file_status: {
        type: 'select', label: 'File Status', required: true,
        options: [
          { value: 'GENERATED', label: 'Generated' },
          { value: 'SENT', label: 'Sent to Bank' },
          { value: 'ACKNOWLEDGED', label: 'Acknowledged' },
          { value: 'PARTIAL_FAIL', label: 'Partially Failed' },
          { value: 'REJECTED', label: 'Rejected by Bank' },
          { value: 'REVERSED', label: 'Reversed' },
        ],
        hint: 'Lifecycle status — engine starts at Generated, ops team updates as bank responds'
      },
      sent_to_bank_date: {
        type: 'date', label: 'Sent to Bank On', required: false,
        hint: 'Set when file is uploaded to bank portal — must be ≥ Generated On'
      },
      bank_ack_ref: {
        type: 'text', label: 'Bank Acknowledgement Ref', required: false,
        maxLength: 50,
        pattern: '^[A-Z0-9\\-/]{6,50}$',
        patternMsg: '6-50 chars: uppercase letters, digits, hyphen, slash only',
        autoUppercase: true,
        hint: 'Required when status = Acknowledged — UTR / batch reference from bank'
      },
      ...DATES,
      ...AUTO,
    },
  },

  // ══ COSTING & GL ════════════════════════════════════════════════════════════

  PAYROLL_COSTING: {
    pk: 'costing_id', pkPrefix: 'COST', pkPad: 5, label: 'Payroll Costing', showPk: true,
    displayColumns: ['run_id', 'assignment_id', 'element_id', 'cost_center_id', 'org_id', 'ledger_id', 'gl_account_code', 'gl_account_name', 'costing_amount'],
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg },
      run_id: { ...FK.payrollRun },
      assignment_id: { ...FK.assignment },
      element_id: { ...FK.element },
      cost_center_id: { type: 'text', label: 'Cost Center Id', required: false, maxLength: 50 },
      org_id: { type: 'text', label: 'Org Id', required: false, maxLength: 50 },
      ledger_id: { type: 'text', label: 'Ledger Id', required: false, maxLength: 50 },
      gl_account_code: { type: 'text', label: 'GL Account Code', required: true, maxLength: 20 },
      gl_account_name: { type: 'text', label: 'GL Account Name', required: true, maxLength: 100 },
      dr_cr: { type: 'text', label: 'DR/CR', required: true, maxLength: 2 },
      costing_amount: { type: 'number', label: 'Costing Amount', required: true, min: 0 },
      currency: { type: 'text', label: 'Currency', required: true, maxLength: 5 },
      gl_posted_flag: { type: 'flag', label: 'GL Posted', required: true },
      ...DATES,
      ...AUTO,
    },
  },

  GL_ACCOUNT_MASTER: {
    pk: 'gl_account_id', pkPrefix: 'GL', pkPad: 5, label: 'GL Account Master',
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg },
      account_code: { type: 'text', label: 'Account Code', required: true, maxLength: 20 },
      account_name: { type: 'text', label: 'Account Name', required: true, maxLength: 100 },
      account_type: { type: 'text', label: 'Account Type', required: true, maxLength: 20 },
      account_category: { type: 'text', label: 'Account Category', required: true, maxLength: 30 },
      normal_balance: { type: 'text', label: 'Normal Balance', required: true, maxLength: 10 },
      ...STATUS,
      ...DATES,
      ...AUTO,
    },
  },

  // ══ STATUTORY ═══════════════════════════════════════════════════════════════

  PF_ACCOUNT: {
    pk: 'pf_id', pkPrefix: 'PF', pkPad: 5, label: 'PF Account', showPk: true,
    displayColumns: ['pf_id', 'assignment_id', 'uan_number', 'pf_member_no', 'monthly_pf_ee', 'monthly_pf_er', 'ytd_pf_ee', 'ytd_pf_er', 'ytd_eps_er', 'account_status', 'effective_from'],
    sections: [
      { id: 'references', label: 'Organisation', fields: ['bg_id', 'COMPANY_id', 'ldg_id'], columns: 3 },
      { id: 'employee', label: 'Employee', fields: ['assignment_id'], columns: 1 },
      { id: 'statutory', label: 'Statutory IDs', fields: ['uan_number', 'pf_member_no', 'pf_estab_code', 'pf_office_code'], columns: 2 },
      { id: 'eps', label: 'EPS Configuration', fields: ['eps_applicable', 'eps_wage_ceiling'], columns: 2 },
      { id: 'monthly', label: 'Monthly Contributions', fields: ['pf_percentage', 'basic_monthly', 'monthly_pf_ee', 'monthly_pf_er', 'monthly_eps_er', 'monthly_edli_er'], columns: 2 },
      { id: 'pfDates', label: 'PF Membership Dates', fields: ['date_joined_pf', 'date_exit_pf'], columns: 2 },
      { id: 'compliance', label: 'Compliance', fields: ['ecr_filed_month', 'account_status', 'ytd_pf_ee', 'ytd_pf_er', 'ytd_eps_er'], columns: 2 },
      { id: 'effective', label: 'Effective Period', fields: ['effective_from', 'effective_to'], columns: 2 },
    ],
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg },
      assignment_id: {
        ...FK.assignment,
        autoFillFromFk: {
          COMPANY_id: 'COMPANY_id',
          bg_id: 'bg_id',
          ldg_id: 'ldg_id',
          pf_percentage: 'pf_percentage',
          annual_ctc: 'annual_ctc',
          basic_monthly: 'basic_monthly'
        },
        hint: 'Selecting assignment auto-fills org context',
        filterFn: (row, existingRecords, record) => {
          if (!existingRecords) return true;
          const hasPf = existingRecords.some(pf => pf.assignment_id === row.assignment_id);
          if (record && record.assignment_id === row.assignment_id) return true;
          return !hasPf;
        }
      },
      uan_number: {
        type: 'text', label: 'UAN Number', required: true,
        maxLength: 12, minLength: 12,
        pattern: '^[0-9]{12}$',
        patternMsg: 'UAN must be exactly 12 digits (e.g. 100123456789)',
        hint: 'Universal Account Number — 12 digits issued by EPFO'
      },
      pf_member_no: {
        type: 'text', label: 'PF Member No.', required: true,
        maxLength: 30,
        pattern: '^[A-Z]{2}\\/[A-Z]{3}\\/[0-9]{7}\\/[0-9]{3}\\/[0-9]{7}$|^[A-Z0-9/-]{6,30}$',
        patternMsg: 'Format: MH/PUN/0012345/000/0123456 or similar EPFO format',
        autoUppercase: true,
        hint: 'EPFO Member ID — state/office/estab/sub-estab/member'
      },
      pf_estab_code: {
        type: 'text', label: 'Establishment Code', required: true,
        maxLength: 20,
        pattern: '^[A-Z]{2}\\/[A-Z]{3}\\/[0-9]+$|^[A-Z0-9/]{4,20}$',
        patternMsg: 'Format: MH/PUN/0012345 (state/office/estab)',
        autoUppercase: true
      },
      pf_office_code: {
        type: 'text', label: 'PF Office Code', required: false,
        maxLength: 5,
        pattern: '^[A-Z]{2,5}$',
        patternMsg: '2-5 uppercase letters (e.g. PUN, MUM, BAN)',
        autoUppercase: true,
        hint: 'Regional EPFO office (e.g. PUN = Pune)'
      },
      eps_applicable: {
        type: 'flag', label: 'EPS Applicable', required: true,
        hint: 'Employees Pension Scheme — usually Y unless wage > ceiling at joining'
      },
      eps_wage_ceiling: {
        type: 'number', label: 'EPS Wage Ceiling (₹)', required: false,
        min: 0, max: 100000, integerOnly: true,
        hint: 'Statutory: ₹15,000 since Sep-2014'
      },
      pf_percentage: {
        type: 'number', label: 'PF %', required: true,
        min: 0, max: 100,
        hint: 'Fetched from Salary Template. Modifying recalculates contribution.',
        validate: (val) => {
          if (val == null || val === 0) return 'No PF percentage defined in assigned salary template (0%). Please update template.';
          return null;
        }
      },
      basic_monthly: {
        type: 'number', label: 'Basic Salary (₹)', required: false,
        readOnly: true,
        hint: 'Auto-fetched from assigned Salary Template / Salary record'
      },
      monthly_pf_ee: {
        type: 'number', label: 'Monthly PF — Employee (₹)', required: false,
        min: 0, max: 999999,
        hint: (formData) => {
          const basic = Number(formData?.basic_monthly) || 0;
          const pct = Number(formData?.pf_percentage) || 0;
          const ee = Math.round(basic * (pct / 100));
          return `₹${basic.toLocaleString('en-IN')} × ${pct}% = ₹${ee.toLocaleString('en-IN')}`;
        },
        readOnly: true,
        recomputeOnChange: true,
        compute: (formData) => {
          const pct = Number(formData.pf_percentage) || 0;
          const basicMonthly = Number(formData.basic_monthly) || 0;
          if (!pct || !basicMonthly) return 0;
          return Math.round(basicMonthly * (pct / 100));
        }
      },
      monthly_pf_er: {
        type: 'number', label: 'Monthly PF — Employer (₹)', required: false,
        min: 0, max: 999999,
        hint: '12% of PF wages (3.67% to PF after EPS share)',
        readOnly: true,
        recomputeOnChange: true,
        compute: (formData) => {
          const pct = Number(formData.pf_percentage) || 0;
          const basicMonthly = Number(formData.basic_monthly) || 0;
          if (!pct || !basicMonthly) return 0;
          const pf_ee = Math.round(basicMonthly * (pct / 100));
          const epsWage = Math.min(basicMonthly, Number(formData.eps_wage_ceiling) || 15000);
          const eps_er = formData.eps_applicable === 'N' ? 0 : Math.round(epsWage * 0.0833);
          return Math.max(0, pf_ee - eps_er);
        }
      },
      monthly_eps_er: {
        type: 'number', label: 'Monthly EPS — Employer (₹)', required: false,
        min: 0, max: 999999,
        hint: '8.33% of PF wages, capped at ₹15,000 × 8.33% = ₹1,250',
        readOnly: true,
        recomputeOnChange: true,
        compute: (formData) => {
          const basicMonthly = Number(formData.basic_monthly) || 0;
          if (!basicMonthly || formData.eps_applicable === 'N') return 0;
          const epsWage = Math.min(basicMonthly, Number(formData.eps_wage_ceiling) || 15000);
          return Math.round(epsWage * 0.0833);
        }
      },
      monthly_edli_er: {
        type: 'number', label: 'Monthly EDLI — Employer (₹)', required: false,
        min: 0, max: 999999,
        hint: '0.50% of PF wages — Employees Deposit Linked Insurance',
        readOnly: true,
        recomputeOnChange: true,
        compute: (formData) => {
          const basicMonthly = Number(formData.basic_monthly) || 0;
          if (!basicMonthly) return 0;
          const edliWage = Math.min(basicMonthly, Number(formData.eps_wage_ceiling) || 15000);
          return Math.round(edliWage * 0.005);
        }
      },
      date_joined_pf: { type: 'date', label: 'Date Joined PF', required: true },
      date_exit_pf: {
        type: 'date', label: 'Date Exit PF', required: false,
        afterField: 'date_joined_pf'
      },
      ecr_filed_month: {
        type: 'text', label: 'Last ECR Filed (Month)', required: false,
        maxLength: 7,
        pattern: '^[0-9]{4}-(0[1-9]|1[0-2])$',
        patternMsg: 'Format YYYY-MM (e.g. 2026-04)',
        hint: 'Electronic Challan-cum-Return — last filed month'
      },
      account_status: {
        type: 'select', label: 'Account Status', required: true,
        options: ['Active', 'Inactive', 'Settled', 'Withdrawn', 'Transferred']
          .map(v => ({ value: v, label: v }))
      },
      ytd_pf_ee: {
        type: 'number', label: 'YTD PF EE (₹)', required: false, defaultValue: 0,
        hint: 'Employee YTD PF Contribution',
        recomputeOnChange: true,
        compute: (formData) => {
          const ctc = Number(formData.annual_ctc) || 0;
          const pct = Number(formData.pf_percentage) || 0;
          if (!ctc || !pct) return 0;
          const basicMonthly = Number(formData.basic_monthly) || ((ctc / 2) / 12);
          return Math.round(basicMonthly * (pct / 100)) * 12;
        }
      },
      ytd_pf_er: {
        type: 'number', label: 'YTD PF ER (₹)', required: false, defaultValue: 0,
        hint: 'Employer YTD PF Contribution',
        recomputeOnChange: true,
        compute: (formData) => {
          const ctc = Number(formData.annual_ctc) || 0;
          const pct = Number(formData.pf_percentage) || 0;
          if (!ctc || !pct) return 0;
          const basicMonthly = Number(formData.basic_monthly) || ((ctc / 2) / 12);
          const pf_ee = Math.round(basicMonthly * (pct / 100));
          const epsWage = Math.min(basicMonthly, Number(formData.eps_wage_ceiling) || 15000);
          const eps_er = formData.eps_applicable === 'N' ? 0 : Math.round(epsWage * 0.0833);
          return Math.max(0, pf_ee - eps_er) * 12;
        }
      },
      ytd_eps_er: {
        type: 'number', label: 'YTD EPS ER (₹)', required: false, defaultValue: 0,
        hint: 'Employer YTD Pension Contribution',
        recomputeOnChange: true,
        compute: (formData) => {
          const ctc = Number(formData.annual_ctc) || 0;
          if (!ctc || formData.eps_applicable === 'N') return 0;
          const basicMonthly = Number(formData.basic_monthly) || ((ctc / 2) / 12);
          const epsWage = Math.min(basicMonthly, Number(formData.eps_wage_ceiling) || 15000);
          return Math.round(epsWage * 0.0833) * 12;
        }
      },
      ...DATES,
      ...AUTO,
    },
  },

  ESI_ACCOUNT: {
    pk: 'esi_id', pkPrefix: 'ESI', pkPad: 5, label: 'ESI Account', showPk: true,
    displayColumns: ['esi_id', 'assignment_id', 'esi_ip_number', 'esi_estab_code', 'monthly_esi_ee', 'monthly_esi_er', 'ytd_esi_ee', 'ytd_esi_er', 'account_status', 'effective_from'],
    sections: [
      { id: 'references', label: 'Organisation', fields: ['bg_id', 'COMPANY_id', 'ldg_id'], columns: 3 },
      { id: 'employee', label: 'Employee', fields: ['assignment_id'], columns: 1 },
      { id: 'statutory', label: 'Statutory IDs', fields: ['esi_ip_number', 'esi_estab_code', 'dispensary_name'], columns: 2 },
      { id: 'wage', label: 'Wage Configuration', fields: ['gross_ceiling', 'esi_ee_percentage', 'esi_er_percentage'], columns: 3 },
      { id: 'monthly', label: 'Monthly Contributions', fields: ['monthly_esi_ee', 'monthly_esi_er'], columns: 2 },
      { id: 'esiDates', label: 'ESI Membership Dates', fields: ['date_joined_esi', 'date_exit_esi'], columns: 2 },
      { id: 'status', label: 'Status', fields: ['account_status', 'ytd_esi_ee', 'ytd_esi_er'], columns: 2 },
      { id: 'effective', label: 'Effective Period', fields: ['effective_from', 'effective_to'], columns: 2 },
    ],
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg },
      assignment_id: {
        ...FK.assignment,
        autoFillFromFk: {
          COMPANY_id: 'COMPANY_id',
          bg_id: 'bg_id',
          ldg_id: 'ldg_id',
          annual_ctc: 'annual_ctc'
        },
        hint: 'Selecting assignment auto-fills org context',
        filterFn: (row, existingRecords, record) => {
          if (!existingRecords) return true;
          const hasEsi = existingRecords.some(esi => esi.assignment_id === row.assignment_id);
          if (record && record.assignment_id === row.assignment_id) return true;
          return !hasEsi;
        }
      },
      esi_ip_number: {
        type: 'text', label: 'ESI IP Number', required: true,
        maxLength: 17, minLength: 17,
        pattern: '^[0-9]{17}$',
        patternMsg: 'IP Number must be exactly 17 digits (e.g. 31000123456000000)',
        hint: 'Insurance Person Number issued by ESIC — 17 digits'
      },
      esi_estab_code: {
        type: 'text', label: 'Establishment Code', required: true,
        maxLength: 17, minLength: 17,
        pattern: '^[0-9]{17}$',
        patternMsg: '17-digit ESIC employer establishment code',
        hint: 'ESIC Code Number assigned to the employer'
      },
      dispensary_name: {
        type: 'text', label: 'Dispensary Name', required: false,
        maxLength: 100,
        pattern: "^[A-Za-z][A-Za-z0-9 .,'\\-/&()]{1,99}$|^$",
        patternMsg: 'Letters, digits, spaces and . , \' - / & ( ) only',
        hint: 'Assigned ESI dispensary / hospital'
      },
      gross_ceiling: {
        type: 'number', label: 'ESI Wage Ceiling (₹)', required: false, readOnly: true,
        min: 0, max: 100000, integerOnly: true,
        hint: 'Statutory: ₹21,000 (since Jan-2017). ESI applicable only if gross ≤ ceiling'
      },
      esi_ee_percentage: {
        type: 'number', label: 'ESI EE %', required: true,
        min: 0, max: 100,
        hint: 'Employee ESI Percentage (default 0.75%)'
      },
      esi_er_percentage: {
        type: 'number', label: 'ESI ER %', required: true,
        min: 0, max: 100,
        hint: 'Employer ESI Percentage (default 3.25%)'
      },
      monthly_esi_ee: {
        type: 'number', label: 'Monthly ESI — Employee (₹)', required: false, readOnly: true,
        min: 0, max: 999999,
        hint: '0.75% of gross wages (since Jul-2019)',
        recomputeOnChange: true,
        compute: (formData) => {
          const ctc = Number(formData.annual_ctc) || 0;
          const pct = Number(formData.esi_ee_percentage) || 0;
          if (!ctc || !pct) return 0;
          const basicMonthly = ctc / 12;
          return Math.ceil(basicMonthly * (pct / 100));
        }
      },
      monthly_esi_er: {
        type: 'number', label: 'Monthly ESI — Employer (₹)', required: false, readOnly: true,
        min: 0, max: 999999,
        hint: '3.25% of gross wages (since Jul-2019)',
        recomputeOnChange: true,
        compute: (formData) => {
          const ctc = Number(formData.annual_ctc) || 0;
          const pct = Number(formData.esi_er_percentage) || 0;
          if (!ctc || !pct) return 0;
          const basicMonthly = ctc / 12;
          return Math.ceil(basicMonthly * (pct / 100));
        }
      },
      date_joined_esi: { type: 'date', label: 'Date Joined ESI', required: true },
      date_exit_esi: {
        type: 'date', label: 'Date Exit ESI', required: false,
        afterField: 'date_joined_esi'
      },
      account_status: {
        type: 'select', label: 'Account Status', required: true,
        options: ['Active', 'Inactive', 'Terminated', 'Transferred', 'Wage Exceeded']
          .map(v => ({ value: v, label: v }))
      },
      ytd_esi_ee: {
        type: 'number', label: 'YTD ESI EE (₹)', required: false, readOnly: true, defaultValue: 0,
        hint: 'Employee YTD ESI Contribution'
      },
      ytd_esi_er: {
        type: 'number', label: 'YTD ESI ER (₹)', required: false, readOnly: true, defaultValue: 0,
        hint: 'Employer YTD ESI Contribution'
      },
      ...DATES,
      ...AUTO,
    },
  },

  // ══ SCHEDULING ══════════════════════════════════════════════════════════════

  WORK_SCHEDULE: {
    pk: 'schedule_id', pkPrefix: 'WS', pkPad: 5, label: 'Work Schedule',
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg },
      schedule_name: { type: 'text', label: 'Schedule Name', required: true, maxLength: 100 },
      schedule_code: { type: 'text', label: 'Schedule Code', required: true, maxLength: 20, pattern: '^[A-Z0-9_]+$', patternMsg: 'Uppercase, numbers, underscores' },
      hours_per_day: { type: 'number', label: 'Hours/Day', required: true, min: 1, max: 24 },
      days_per_week: { type: 'number', label: 'Days/Week', required: true, min: 1, max: 7, integerOnly: true },
      working_days_per_month: { type: 'number', label: 'Working Days/Month', required: true, min: 1, max: 31, integerOnly: true },
      start_time: { type: 'text', label: 'Start Time', required: false, maxLength: 10 },
      end_time: { type: 'text', label: 'End Time', required: false, maxLength: 10 },
      break_minutes: { type: 'number', label: 'Break (min)', required: false, min: 0, max: 120, integerOnly: true },
      ...STATUS,
      ...DATES,
      ...AUTO,
    },
  },

  SHIFT_DEFINITION: {
    pk: 'shift_id', pkPrefix: 'SH', pkPad: 5, label: 'Shift Definition',
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg },
      schedule_id: { ...FK.workSchedule },
      shift_name: { type: 'text', label: 'Shift Name', required: true, maxLength: 50 },
      shift_code: { type: 'text', label: 'Shift Code', required: true, maxLength: 20 },
      shift_start: { type: 'time', label: 'Start Time', required: false },
      shift_end: { type: 'time', label: 'End Time', required: false, afterTimeField: 'shift_start' },
      shift_type: { type: 'text', label: 'Shift Type', required: true, maxLength: 20 },
      ot_multiplier: { type: 'number', label: 'OT Multiplier', required: false, min: 1 },
      ...STATUS,
      ...DATES,
      ...AUTO,
    },
  },

  HOLIDAY_CALENDAR: {
    pk: 'holiday_id', pkPrefix: 'HOL', pkPad: 5, label: 'Holiday Calendar',
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg },
      location_id: { ...FK.location, required: false },
      holiday_date: { type: 'date', label: 'Holiday Date', required: true },
      holiday_name: { type: 'text', label: 'Holiday Name', required: true, maxLength: 100 },
      holiday_type: { type: 'text', label: 'Holiday Type', required: true, maxLength: 20 },
      applicable_to: { type: 'text', label: 'Applicable To', required: false, maxLength: 30 },
      ...STATUS,
      ...DATES,
      ...AUTO,
    },
  },

  ABSENCE_TYPE: {
    pk: 'absence_type_id', pkPrefix: 'ABS', pkPad: 5, label: 'Absence Type',
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg },
      absence_name: { type: 'text', label: 'Absence Name', required: true, maxLength: 50 },
      absence_code: { type: 'text', label: 'Absence Code', required: true, maxLength: 20, pattern: '^[A-Z_]+$', patternMsg: 'Uppercase letters and underscores' },
      category: { type: 'text', label: 'Category', required: true, maxLength: 20 },
      paid_flag: { type: 'flag', label: 'Paid', required: true },
      encashable_flag: { type: 'flag', label: 'Encashable', required: true },
      carry_forward_flag: { type: 'flag', label: 'Carry Forward', required: true },
      max_carry_forward_days: { type: 'number', label: 'Max Carry Forward Days', required: false, min: 0, max: 365, integerOnly: true },
      annual_entitlement: { type: 'number', label: 'Annual Entitlement', required: true, min: 0, max: 365 },
      gender_specific: { type: 'text', label: 'Gender Specific', required: false, maxLength: 10 },
      ...STATUS,
      ...DATES,
      ...AUTO,
    },
  },

  // ══ MASTERS ══════════════════════════════════════════════════════════════════

  BANK_MASTER: {
    pk: 'bank_id', pkPrefix: 'BNK', pkPad: 5, label: 'Bank Master', showPk: true,
    displayColumns: ['bank_id', 'bank_name', 'bank_code', 'ifsc_prefix', 'micr_code', 'swift_code', 'bank_type', 'active_flag'],
    sections: [
      { id: 'references', label: 'Organisation', fields: ['bg_id', 'COMPANY_id'], columns: 2 },
      { id: 'identity', label: 'Bank Identity', fields: ['bank_name', 'bank_code', 'bank_type'], columns: 2 },
      { id: 'codes', label: 'Banking Codes', fields: ['ifsc_prefix', 'micr_code', 'swift_code'], columns: 3 },
      { id: 'compliance', label: 'Compliance', fields: ['rbi_registration'], columns: 2 },
      { id: 'effective', label: 'Effective Period', fields: ['effective_from', 'effective_to'], columns: 2 },
      { id: 'status', label: 'Status', fields: ['active_flag'], columns: 2 },
    ],
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      bank_name: {
        type: 'text', label: 'Bank Name', required: true,
        minLength: 2, maxLength: 100,
        pattern: '^[A-Za-z0-9 &.,()\\-]+$',
        patternMsg: 'Letters, numbers, spaces and & . , ( ) - allowed'
      },
      bank_code: {
        type: 'text', label: 'Bank Code', required: true,
        minLength: 2, maxLength: 10,
        pattern: '^[A-Z0-9]{2,10}$',
        patternMsg: '2–10 uppercase letters/digits (e.g. HDFC, SBI, ICICI)',
        autoUppercase: true,
        autoGenFrom: 'bank_name', autoGenStyle: 'first_letters',
        hint: 'Auto-generated from Bank Name (first letters of each word)'
      },
      ifsc_prefix: {
        type: 'text', label: 'IFSC Prefix', required: true,
        minLength: 4, maxLength: 4,
        pattern: '^[A-Z]{4}$',
        patternMsg: 'Exactly 4 uppercase letters (e.g. HDFC, SBIN, UTIB, ICIC)',
        autoUppercase: true,
        hint: 'First 4 chars of IFSC — bank identifier'
      },
      micr_code: {
        type: 'text', label: 'MICR Code', required: false,
        minLength: 9, maxLength: 9,
        pattern: '^[0-9]{9}$',
        patternMsg: 'MICR is exactly 9 digits (city + bank + branch)',
        hint: 'Magnetic Ink Character Recognition — 9-digit code printed on cheques'
      },
      swift_code: {
        type: 'text', label: 'SWIFT / BIC Code', required: false,
        minLength: 8, maxLength: 11,
        pattern: '^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$',
        patternMsg: '8 or 11 chars: bank(4) + country(2) + location(2) [+ branch(3)]',
        autoUppercase: true,
        hint: 'BIC code for international transfers (e.g. HDFCINBB, HDFCINBBXXX)'
      },
      bank_type: {
        type: 'select', label: 'Bank Type', required: true,
        options: [
          'Scheduled Commercial',
          'Public Sector',
          'Private Sector',
          'Foreign',
          'Cooperative',
          'Regional Rural (RRB)',
          'Small Finance',
          'Payments',
        ].map(v => ({ value: v, label: v }))
      },
      rbi_registration: {
        type: 'text', label: 'RBI Registration No.', required: false,
        maxLength: 30,
        pattern: '^[A-Z0-9\\-]{4,30}$',
        patternMsg: 'Uppercase letters, digits, and hyphens only (e.g. RBI-SCB-0001)',
        autoUppercase: true,
        hint: 'RBI licence / registration number'
      },
      ...STATUS,
      ...DATES,
      ...AUTO,
    },
  },

  TAX_AUTHORITY: {
    pk: 'authority_id', pkPrefix: 'TA', pkPad: 5, label: 'Tax Authority', showPk: true,
    displayColumns: ['authority_id', 'authority_name', 'authority_code', 'authority_type', 'jurisdiction', 'registration_number', 'active_flag'],
    sections: [
      { id: 'references', label: 'Organisation', fields: ['bg_id', 'COMPANY_id'], columns: 2 },
      { id: 'identity', label: 'Authority Identity', fields: ['authority_name', 'authority_code', 'authority_type'], columns: 2 },
      { id: 'regdetails', label: 'Jurisdiction & Registration', fields: ['jurisdiction', 'registration_number'], columns: 2 },
      { id: 'contact', label: 'Contact', fields: ['contact_address', 'portal_url'], columns: 2 },
      { id: 'effective', label: 'Effective Period', fields: ['effective_from', 'effective_to'], columns: 2 },
      { id: 'status', label: 'Status', fields: ['active_flag'], columns: 2 },
    ],
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      authority_name: {
        type: 'text', label: 'Authority Name', required: true,
        minLength: 2, maxLength: 100,
        pattern: '^[A-Za-z0-9 &.,()\\-]+$',
        patternMsg: 'Letters, numbers, spaces and & . , ( ) - allowed',
        hint: 'Full name (e.g. Employees Provident Fund Organisation)'
      },
      authority_code: {
        type: 'text', label: 'Authority Code', required: true,
        minLength: 2, maxLength: 20,
        pattern: '^[A-Z0-9\\-]{2,20}$',
        patternMsg: '2–20 uppercase letters, digits, hyphens (e.g. EPFO, ESIC, ITD, GST-MH)',
        autoUppercase: true,
        hint: 'Short identifier — used internally'
      },
      authority_type: {
        type: 'select', label: 'Authority Type', required: true,
        options: [
          'PF Authority',
          'ESI Authority',
          'TDS Authority',
          'Income Tax Authority',
          'PT Authority',
          'GST Authority',
          'Labour Welfare Fund',
          'Gratuity Trust',
          'Other',
        ].map(v => ({ value: v, label: v }))
      },
      jurisdiction: {
        type: 'text', label: 'Jurisdiction', required: true,
        maxLength: 60,
        hint: 'e.g. Pan India, Maharashtra, Pune Region'
      },
      registration_number: {
        type: 'text', label: 'Registration Number', required: true,
        minLength: 3, maxLength: 30,
        pattern: '^[A-Z0-9\\-/]{3,30}$',
        patternMsg: 'Uppercase letters, digits, hyphens, slashes (e.g. MAHPUN0012345, 27ABCDE1234F1Z5)',
        autoUppercase: true,
        hint: 'Format varies by authority: TAN (PNEV12345A), ESIC code (17 digits), GSTIN (15 chars), PF Estab (MH/PUN/123)'
      },
      contact_address: {
        type: 'text', label: 'Contact Address', required: false,
        maxLength: 200,
        pattern: "^[A-Za-z0-9 .,'#\\-/()&]{1,200}$|^$",
        patternMsg: 'Letters, digits, spaces and . , \' # - / ( ) & only',
        hint: 'Office address for correspondence'
      },
      portal_url: {
        type: 'url', label: 'Portal URL', required: false,
        maxLength: 150,
        ...PATTERNS.URL,
        hint: 'Official website / e-filing portal (e.g. https://epfindia.gov.in)'
      },
      ...STATUS,
      ...DATES,
      ...AUTO,
    },
  },


  TAX_REGIME_CONFIG: {
    pk: 'config_id', pkPrefix: 'TRC', pkPad: 5, label: 'Tax Regime Config', showPk: true,
    displayColumns: ['config_id', 'assignment_id', 'fiscal_year', 'tax_regime', 'projected_taxable_income', 'tds_monthly', 'regime_locked', 'effective_from'],
    sections: [
      { id: 'references', label: 'Organisation', fields: ['bg_id', 'COMPANY_id', 'ldg_id'], columns: 3 },
      { id: 'employee', label: 'Employee & Period', fields: ['person_id', 'assignment_id', 'fiscal_year'], columns: 2 },
      { id: 'regime', label: 'Tax Regime Selection', fields: ['tax_regime', 'regime_selected_date', 'regime_locked'], columns: 2 },
      { id: 'projection', label: 'Tax Projections', fields: ['projected_taxable_income', 'standard_deduction', 'projected_tax', 'tds_monthly'], columns: 2 },
      { id: 'effective', label: 'Effective Period', fields: ['effective_from', 'effective_to'], columns: 2 },
    ],
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg },
      person_id: {
        ...FK.person,
        autoSelectFrom: { assignment_id: 'person_id' },
        hint: 'Picking a Person auto-selects their Assignment (first match)'
      },
      assignment_id: {
        ...FK.assignment,
        autoFillFromFk: {
          COMPANY_id: 'COMPANY_id',
          bg_id: 'bg_id',
          ldg_id: 'ldg_id',
          person_id: 'person_id',
        },
        hint: 'Selecting assignment auto-fills Person and org context'
      },
      fiscal_year: {
        type: 'text', label: 'Fiscal Year', required: true,
        minLength: 7, maxLength: 7,
        ...PATTERNS.FY,
        hint: 'Indian FY runs April to March (e.g. 2026-27)'
      },
      tax_regime: {
        type: 'select', label: 'Tax Regime', required: true,
        options: [
          { value: 'OLD', label: 'Old Regime (with deductions)' },
          { value: 'NEW', label: 'New Regime (lower rates, no deductions)' },
        ],
        hint: 'Once payroll runs, regime is locked for the FY'
      },
      regime_selected_date: {
        type: 'date', label: 'Regime Selected Date', required: true,
        hint: 'Date employee chose this regime'
      },
      regime_locked: {
        type: 'flag', label: 'Regime Locked', required: true,
        hint: 'Y after first payroll run — switch not allowed mid-year'
      },
      projected_taxable_income: {
        type: 'number', label: 'Projected Taxable Income (₹)', required: false,
        min: 0, max: 99999999,
        hint: 'Annual gross − exemptions − deductions'
      },
      standard_deduction: {
        type: 'number', label: 'Standard Deduction (₹)', required: false,
        min: 0, max: 100000, integerOnly: true,
        hint: 'Old: ₹50,000 · New: ₹75,000 (FY 2024-25+)'
      },
      projected_tax: {
        type: 'number', label: 'Projected Annual Tax (₹)', required: false,
        min: 0, max: 99999999,
        hint: 'Slab tax + surcharge + 4% cess'
      },
      tds_monthly: {
        type: 'number', label: 'Monthly TDS (₹)', required: false,
        min: 0, max: 999999,
        hint: 'Projected annual tax / 12 — deducted each payroll cycle'
      },
      ...DATES,
      ...AUTO,
    },
  },

  // ══ LOOKUP MASTERS (Phase 2 — added for dropdowns/LOVs) ═════════════════════

  ASSIGNMENT_STATUS_MASTER: {
    pk: 'status_id', pkPrefix: 'AST', pkPad: 3, label: 'Assignment Status', showPk: true,
    displayColumns: ['status_id', 'status_code', 'status_name', 'sort_order', 'active_flag'],
    sections: [
      { id: 'identity', label: 'Status Definition', fields: ['status_code', 'status_name', 'description', 'sort_order'], columns: 2 },
      { id: 'meta', label: 'Status & Period', fields: ['active_flag', 'effective_from'], columns: 2 },
    ],
    fields: {
      status_code: { type: 'text', label: 'Status Code', required: true, maxLength: 30, ...PATTERNS.ALPHA, autoUppercase: true },
      status_name: { type: 'text', label: 'Status Name', required: true, maxLength: 60, ...PATTERNS.NAME },
      description: { type: 'text', label: 'Description', required: false, maxLength: 200 },
      sort_order: { type: 'number', label: 'Sort Order', required: false, min: 1, max: 999, integerOnly: true },
      active_flag: { type: 'flag', label: 'Status', required: true },
      effective_from: { type: 'date', label: 'Effective From', required: true },
      ...AUTO,
    },
  },

  EMPLOYMENT_TYPE_MASTER: {
    pk: 'type_id', pkPrefix: 'EMT', pkPad: 3, label: 'Employment Type', showPk: true,
    displayColumns: ['type_id', 'type_code', 'type_name', 'sort_order', 'active_flag'],
    sections: [
      { id: 'identity', label: 'Type Definition', fields: ['type_code', 'type_name', 'description', 'sort_order'], columns: 2 },
      { id: 'meta', label: 'Status & Period', fields: ['active_flag', 'effective_from'], columns: 2 },
    ],
    fields: {
      type_code: { type: 'text', label: 'Type Code', required: true, maxLength: 30, ...PATTERNS.ALPHA, autoUppercase: true },
      type_name: { type: 'text', label: 'Type Name', required: true, maxLength: 60, ...PATTERNS.NAME },
      description: { type: 'text', label: 'Description', required: false, maxLength: 200 },
      sort_order: { type: 'number', label: 'Sort Order', required: false, min: 1, max: 999, integerOnly: true },
      active_flag: { type: 'flag', label: 'Status', required: true },
      effective_from: { type: 'date', label: 'Effective From', required: true },
      ...AUTO,
    },
  },

  RELATIONSHIP_TYPE_MASTER: {
    pk: 'rel_type_id', pkPrefix: 'RLT', pkPad: 3, label: 'Relationship Type', showPk: true,
    displayColumns: ['rel_type_id', 'rel_code', 'rel_name', 'sort_order', 'active_flag'],
    sections: [
      { id: 'identity', label: 'Relationship Definition', fields: ['rel_code', 'rel_name', 'description', 'sort_order'], columns: 2 },
      { id: 'meta', label: 'Status & Period', fields: ['active_flag', 'effective_from'], columns: 2 },
    ],
    fields: {
      rel_code: { type: 'text', label: 'Relationship Code', required: true, maxLength: 30, ...PATTERNS.ALPHA, autoUppercase: true },
      rel_name: { type: 'text', label: 'Relationship Name', required: true, maxLength: 60, ...PATTERNS.NAME },
      description: { type: 'text', label: 'Description', required: false, maxLength: 200 },
      sort_order: { type: 'number', label: 'Sort Order', required: false, min: 1, max: 999, integerOnly: true },
      active_flag: { type: 'flag', label: 'Status', required: true },
      effective_from: { type: 'date', label: 'Effective From', required: true },
      ...AUTO,
    },
  },

  PROOF_TYPE_MASTER: {
    pk: 'proof_type_id', pkPrefix: 'PRF', pkPad: 3, label: 'Proof Type', showPk: true,
    displayColumns: ['proof_type_id', 'proof_code', 'proof_name', 'section_code', 'sort_order', 'active_flag'],
    sections: [
      { id: 'identity', label: 'Proof Definition', fields: ['proof_code', 'proof_name', 'section_code', 'description', 'sort_order'], columns: 2 },
      { id: 'meta', label: 'Status & Period', fields: ['active_flag', 'effective_from'], columns: 2 },
    ],
    fields: {
      proof_code: { type: 'text', label: 'Proof Code', required: true, maxLength: 30, pattern: '^[A-Z_]+$', patternMsg: 'Uppercase letters and underscores only', autoUppercase: true },
      proof_name: { type: 'text', label: 'Proof Name', required: true, maxLength: 80, ...PATTERNS.NAME },
      section_code: { type: 'text', label: 'Section Code', required: false, maxLength: 20, hint: 'IT Act section: 80C, 80D, 24(b), 10(13A) etc.' },
      description: { type: 'text', label: 'Description', required: false, maxLength: 200 },
      sort_order: { type: 'number', label: 'Sort Order', required: false, min: 1, max: 999, integerOnly: true },
      active_flag: { type: 'flag', label: 'Status', required: true },
      effective_from: { type: 'date', label: 'Effective From', required: true },
      ...AUTO,
    },
  },

  TAX_DECLARATION_STATUS_MASTER: {
    pk: 'status_id', pkPrefix: 'TDS', pkPad: 3, label: 'Tax Declaration Status', showPk: true,
    displayColumns: ['status_id', 'status_code', 'status_name', 'sort_order', 'active_flag'],
    sections: [
      { id: 'identity', label: 'Status Definition', fields: ['status_code', 'status_name', 'description', 'sort_order'], columns: 2 },
      { id: 'meta', label: 'Status & Period', fields: ['active_flag', 'effective_from'], columns: 2 },
    ],
    fields: {
      status_code: { type: 'text', label: 'Status Code', required: true, maxLength: 30, ...PATTERNS.ALPHA, autoUppercase: true },
      status_name: { type: 'text', label: 'Status Name', required: true, maxLength: 60, ...PATTERNS.NAME },
      description: { type: 'text', label: 'Description', required: false, maxLength: 200 },
      sort_order: { type: 'number', label: 'Sort Order', required: false, min: 1, max: 999, integerOnly: true },
      active_flag: { type: 'flag', label: 'Status', required: true },
      effective_from: { type: 'date', label: 'Effective From', required: true },
      ...AUTO,
    },
  },

  FORM16_GENERATION_STATUS_MASTER: {
    pk: 'status_id', pkPrefix: 'F16', pkPad: 3, label: 'Form 16 Generation Status', showPk: true,
    displayColumns: ['status_id', 'status_code', 'status_name', 'sort_order', 'active_flag'],
    sections: [
      { id: 'identity', label: 'Status Definition', fields: ['status_code', 'status_name', 'description', 'sort_order'], columns: 2 },
      { id: 'meta', label: 'Status & Period', fields: ['active_flag', 'effective_from'], columns: 2 },
    ],
    fields: {
      status_code: { type: 'text', label: 'Status Code', required: true, maxLength: 30, ...PATTERNS.ALPHA, autoUppercase: true },
      status_name: { type: 'text', label: 'Status Name', required: true, maxLength: 60, ...PATTERNS.NAME },
      description: { type: 'text', label: 'Description', required: false, maxLength: 200 },
      sort_order: { type: 'number', label: 'Sort Order', required: false, min: 1, max: 999, integerOnly: true },
      active_flag: { type: 'flag', label: 'Status', required: true },
      effective_from: { type: 'date', label: 'Effective From', required: true },
      ...AUTO,
    },
  },

  GRADE_MASTER: {
    pk: 'grade_id', pkPrefix: 'GRD', pkPad: 3, label: 'Grade Master', showPk: true,
    displayColumns: ['grade_id', 'grade_code', 'grade_name', 'grade_level', 'min_salary', 'max_salary', 'active_flag'],
    sections: [
      { id: 'identity', label: 'Grade Definition', fields: ['grade_code', 'grade_name', 'grade_level', 'description'], columns: 2 },
      { id: 'salary', label: 'Salary Band', fields: ['min_salary', 'max_salary'], columns: 2 },
      { id: 'meta', label: 'Status & Period', fields: ['active_flag', 'effective_from'], columns: 2 },
    ],
    fields: {
      grade_code: { type: 'text', label: 'Grade Code', required: true, maxLength: 10, pattern: '^[A-Z][A-Z0-9]{0,9}$', patternMsg: 'Uppercase letters/digits (e.g. G1, M5, EXEC)', autoUppercase: true },
      grade_name: { type: 'text', label: 'Grade Name', required: true, maxLength: 60, ...PATTERNS.NAME },
      grade_level: { type: 'number', label: 'Grade Level', required: true, min: 1, max: 99, integerOnly: true, hint: 'Numeric hierarchy level (1 = lowest)' },
      description: { type: 'text', label: 'Description', required: false, maxLength: 200 },
      min_salary: { type: 'number', label: 'Min Salary (₹)', required: false, min: 0, max: 99999999, hint: 'Minimum monthly CTC for this grade' },
      max_salary: { type: 'number', label: 'Max Salary (₹)', required: false, min: 0, max: 99999999, minRefField: 'min_salary', hint: 'Maximum monthly CTC for this grade' },
      active_flag: { type: 'flag', label: 'Status', required: true },
      effective_from: { type: 'date', label: 'Effective From', required: true },
      ...AUTO,
    },
  },

  // ══ WORKFORCE ═══════════════════════════════════════════════════════════════

  PERSON: {
    pk: 'person_id', pkPrefix: 'PER', pkPad: 3, label: 'Person',
    sections: [
      { id: 'org', label: 'Organisation', fields: ['bg_id', 'COMPANY_id', 'ldg_id'], columns: 3 },
      { id: 'identity', label: 'Identity', fields: ['first_name', 'middle_name', 'last_name', 'full_name'], columns: 2 },
      { id: 'personal', label: 'Personal Details', fields: ['date_of_birth', 'gender', 'marital_status', 'blood_group', 'nationality', 'mother_tongue'], columns: 3 },
      { id: 'contact', label: 'Contact', fields: ['personal_email', 'work_email', 'mobile_no', 'alternate_no'], columns: 2 },
      { id: 'address', label: 'Address', fields: ['current_address', 'permanent_address'], columns: 1 },
      { id: 'idproof', label: 'ID Proofs', fields: ['aadhar_no', 'pan_no', 'passport_no'], columns: 3 },
      { id: 'emergency', label: 'Emergency Contact', fields: ['emergency_contact_name', 'emergency_contact_no'], columns: 2 },
      { id: 'media', label: 'Photo', fields: ['photo_url'], columns: 1 },
      { id: 'metadata', label: 'Status', fields: ['active_flag', 'effective_from', 'effective_to'], columns: 3 },
    ],
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id', 'ldg_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        cascade: ['ldg_id'],
        autoFillFromFk: { ldg_id: 'default_ldg_id' },
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: {
        ...FK.ldg,
        readOnly: true,
        hint: 'Auto-filled from the selected company — not editable'
      },
      first_name: {
        type: 'text', label: 'First Name', required: true, maxLength: 50,
        ...PATTERNS.NAME
      },
      middle_name: {
        type: 'text', label: 'Middle Name', required: false, maxLength: 50,
        pattern: "^[A-Za-z][A-Za-z .'\\-]{0,49}$|^$",
        patternMsg: PATTERNS.NAME.patternMsg
      },
      last_name: {
        type: 'text', label: 'Last Name', required: true, maxLength: 50,
        ...PATTERNS.NAME
      },
      full_name: {
        type: 'text', label: 'Full Name', required: true, maxLength: 150,
        readOnly: true,
        recomputeOnChange: true,
        compute: (form) => [form.first_name, form.middle_name, form.last_name]
          .map(s => String(s || '').trim())
          .filter(Boolean)
          .join(' '),
        hint: 'Auto-filled from First + Middle + Last'
      },
      date_of_birth: {
        type: 'date', label: 'Date of Birth', required: true,
        noFuture: true,
        hint: 'Format: YYYY-MM-DD. Future dates are not allowed.'
      },
      gender: {
        type: 'select', label: 'Gender', required: true,
        options: [{ value: 'Male', label: 'Male' }, { value: 'Female', label: 'Female' }, { value: 'Other', label: 'Other' }]
      },
      marital_status: {
        type: 'select', label: 'Marital Status', required: false,
        options: [{ value: 'Single', label: 'Single' }, { value: 'Married', label: 'Married' }, { value: 'Divorced', label: 'Divorced' }, { value: 'Widowed', label: 'Widowed' }]
      },
      blood_group: {
        type: 'select', label: 'Blood Group', required: false,
        options: ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(v => ({ value: v, label: v }))
      },
      nationality: {
        type: 'select', label: 'Nationality', required: false,
        options: ['Indian', 'American', 'British', 'Australian', 'Canadian', 'Singaporean', 'UAE', 'German', 'French', 'Japanese', 'Chinese', 'Other'].map(v => ({ value: v, label: v }))
      },
      mother_tongue: {
        type: 'select', label: 'Mother Tongue', required: false,
        options: ['Hindi', 'English', 'Marathi', 'Tamil', 'Telugu', 'Kannada', 'Malayalam', 'Bengali', 'Gujarati', 'Punjabi', 'Urdu', 'Odia', 'Assamese', 'Other'].map(v => ({ value: v, label: v }))
      },
      personal_email: {
        type: 'email', label: 'Personal Email', required: true, maxLength: 100,
        ...PATTERNS.EMAIL
      },
      work_email: {
        type: 'email', label: 'Work Email', required: false, maxLength: 100,
        ...PATTERNS.EMAIL
      },
      mobile_no: {
        type: 'text', label: 'Mobile No', required: true, maxLength: 15,
        ...PATTERNS.MOBILE_IN,
        hint: 'Indian 10-digit mobile (starts with 6/7/8/9), optional +91 prefix'
      },
      alternate_no: {
        type: 'text', label: 'Alternate No', required: false, maxLength: 15,
        pattern: '^(\\+91[\\-\\s]?)?[6-9][0-9]{9}$|^$',
        patternMsg: PATTERNS.MOBILE_IN.patternMsg
      },
      current_address: {
        type: 'text', label: 'Current Address', required: true, maxLength: 250,
        pattern: "^[A-Za-z0-9 .,'#\\-/()&]{1,250}$",
        patternMsg: 'Letters, digits, spaces and . , \' # - / ( ) & only'
      },
      permanent_address: {
        type: 'text', label: 'Permanent Address', required: true, maxLength: 250,
        pattern: "^[A-Za-z0-9 .,'#\\-/()&]{1,250}$",
        patternMsg: 'Letters, digits, spaces and . , \' # - / ( ) & only'
      },
      aadhar_no: {
        type: 'text', label: 'Aadhar No', required: true, maxLength: 12, minLength: 12,
        ...PATTERNS.AADHAAR,
        hint: '12-digit number issued by UIDAI'
      },
      pan_no: {
        type: 'text', label: 'PAN', required: true, maxLength: 10, minLength: 10,
        ...PATTERNS.PAN,
        autoUppercase: true,
        hint: 'Format: ABCDE1234F (5 letters + 4 digits + 1 letter)'
      },
      passport_no: {
        type: 'text', label: 'Passport No', required: false, maxLength: 20,
        pattern: '^[A-Za-z][0-9]{7}$|^$',
        patternMsg: 'Valid passport number (e.g. N2345678) or empty',
        autoUppercase: true
      },
      emergency_contact_name: {
        type: 'text', label: 'Emergency Contact Name', required: false, maxLength: 100,
        pattern: "^[A-Za-z][A-Za-z .'\\-]{0,99}$|^$",
        patternMsg: PATTERNS.NAME.patternMsg
      },
      emergency_contact_no: {
        type: 'text', label: 'Emergency Contact No', required: false, maxLength: 15,
        pattern: '^(\\+91[\\-\\s]?)?[6-9][0-9]{9}$|^$',
        patternMsg: PATTERNS.MOBILE_IN.patternMsg
      },
      photo_url: {
        type: 'url', label: 'Photo URL', required: false, maxLength: 250,
        ...PATTERNS.URL,
        hint: 'Direct link to photo (https://...)'
      },
      ...STATUS,
      ...DATES,
      ...AUTO,
    },
  },

  ASSIGNMENT: {
    pk: 'assignment_id', pkPrefix: 'ASN', pkPad: 3, label: 'Assignment',
    validate: (formData, existingRecords) => {
      const errs = {};
      const dup = existingRecords.find(r => String(r.person_id) === String(formData.person_id) && String(r.assignment_id) !== String(formData.assignment_id));
      if (dup) errs.person_id = 'This person is already assigned to another record.';
      return errs;
    },
    sections: [
      { id: 'org', label: 'Organisation', fields: ['bg_id', 'COMPANY_id', 'ldg_id'], columns: 3 },
      { id: 'employee', label: 'Employee', fields: ['person_id', 'employee_code', 'manager_id'], columns: 3 },
      { id: 'job', label: 'Job Details', fields: ['business_type_id', 'department_id', 'designation_id', 'location_id'], columns: 2 },
      { id: 'payroll', label: 'Payroll Setup', fields: ['payroll_id', 'default_template_id'], columns: 2 },
      { id: 'schedule', label: 'Schedule', fields: ['work_schedule_id', 'shift_id'], columns: 2 },
      { id: 'employment', label: 'Employment Details', fields: ['employment_type', 'hire_date', 'start_date', 'end_date', 'probation_end_date', 'notice_period_days'], columns: 2 },
      { id: 'metadata', label: 'Status', fields: ['assignment_status', 'active_flag', 'effective_from', 'effective_to'], columns: 2 },
    ],
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id', 'business_type_id', 'department_id', 'designation_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        cascade: ['business_type_id', 'department_id', 'designation_id'],
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      ldg_id: { ...FK.ldg },
      person_id: {
        type: 'dropdown', label: 'Person', required: true,
        source: 'PERSON', valueField: 'person_id', labelField: 'full_name',
        labelCombo: ['person_id', 'full_name'],
        filterFn: (row, existingRecords, currentRecord) => {
          return !existingRecords.some(r =>
            String(r.person_id) === String(row.person_id) &&
            (!currentRecord || String(r.assignment_id) !== String(currentRecord.assignment_id))
          );
        }
      },
      employee_code: {
        type: 'text', label: 'Employee Code', required: true, maxLength: 20,
        readOnly: true,
        pattern: '^[A-Z][A-Z0-9\\-]{1,19}$',
        patternMsg: 'Uppercase letters/digits/hyphens, must start with letter (e.g. EMP-001, VVS001)',
        autoUppercase: true,
        hint: 'Auto-generated based on selected Company'
      },
      manager_id: {
        type: 'dropdown', label: 'Manager (Person)', required: false,
        source: 'PERSON', valueField: 'person_id', labelField: 'full_name',
        labelCombo: ['person_id', 'full_name'],
        excludeIfMatches: 'person_id',
        hint: 'An employee cannot be their own manager'
      },
      business_type_id: {
        ...FK.businessType,
        required: false,
        dependsOn: 'COMPANY_id',
        cascade: ['department_id', 'designation_id'],
        filterVia: {
          field: 'COMPANY_id',
          parentKey: 'company_id',
          valueKey: 'business_type_id'
        }
      },
      department_id: {
        ...FK.department,
        dependsOn: 'business_type_id',
        filterVia: {
          field: 'business_type_id',
          parentKey: 'business_type_id',
          valueKey: 'department_ids'
        }
      },
      designation_id: {
        ...FK.designation,
        dependsOn: 'business_type_id',
        filterVia: {
          field: 'business_type_id',
          parentKey: 'business_type_id',
          valueKey: 'designation_ids'
        }
      },
      location_id: { ...FK.location, required: false },
      payroll_id: { ...FK.payroll },
      default_template_id: {
        type: 'dropdown', label: 'Default Salary Template', required: false,
        source: 'SALARY_TEMPLATE', valueField: 'template_id', labelField: 'template_name',
        labelCombo: ['template_id', 'template_name']
      },
      work_schedule_id: { ...FK.workSchedule },
      shift_id: {
        type: 'dropdown', label: 'Shift', required: false,
        source: 'SHIFT_DEFINITION', valueField: 'shift_id', labelField: 'shift_name'
      },
      employment_type: {
        ...FK.employmentType, required: true,
        hint: 'Sourced from Employment Type Master'
      },
      hire_date: {
        type: 'date', label: 'Hire Date', required: true,
        hint: 'Date employee joined the company'
      },
      start_date: {
        type: 'date', label: 'Start Date', required: true,
        hint: 'Assignment start date (may differ from hire date for transfers)'
      },
      end_date: {
        type: 'date', label: 'End Date', required: false,
        afterField: 'start_date'
      },
      probation_end_date: {
        type: 'date', label: 'Probation End Date', required: false,
        afterField: 'hire_date'
      },
      notice_period_days: {
        type: 'number', label: 'Notice Period (Days)', required: false,
        min: 0, max: 120, integerOnly: true,
        hint: 'Typically 30/60/90 days (max 120)'
      },
      assignment_status: {
        ...FK.assignmentStatus, required: true,
        hint: 'Sourced from Assignment Status Master'
      },
      ...STATUS,
      ...DATES,
      ...AUTO,
    },
  },

  FULL_FINAL_SETTLEMENT: {
    pk: 'ff_id', pkPrefix: 'FFS', pkPad: 3, label: 'Full & Final Settlement', showPk: true,
    displayColumns: ['ff_id', 'employee_id', 'exit_date', 'net_payable', 'status'],
    displayJoins: {
      employee_id: { source: 'PERSON', valueField: 'person_id', labelCombo: ['person_id', 'full_name'] },
      assignment_id: { source: 'ASSIGNMENT', valueField: 'assignment_id', labelCombo: ['assignment_id', 'employee_code'] },
      linked_payroll_run_id: { source: 'PAYROLL_RUN', valueField: 'run_id', labelField: 'run_name' },
    },
    sections: [
      { id: 'identity', label: 'Organization & Identity', fields: ['bg_id', 'COMPANY_id', 'employee_id', 'assignment_id'], columns: 2 },
      { id: 'timeline', label: 'Timeline & Reference', fields: ['resignation_date', 'notice_start_date', 'notice_period_days', 'exit_date', 'linked_payroll_run_id'], columns: 2 },
      { id: 'summary', label: 'Totals & Summary', fields: ['gross_settlement', 'total_earnings', 'total_deductions', 'total_recovery', 'net_payable'], columns: 3 },
      { id: 'recovery_strategy', label: 'Recovery Strategy', fields: ['recovery_lines'], columns: 1 },
      { id: 'metadata', label: 'Status & Controls', fields: ['status', 'lifecycle_status'], columns: 2 },
    ],
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      employee_id: {
        type: 'dropdown', label: 'Employee', required: true,
        source: 'PERSON', valueField: 'person_id', labelField: 'full_name',
        labelCombo: ['person_id', 'full_name'],
        autoSelectFrom: { assignment_id: 'person_id' },
        hint: 'Select the employee to offboard'
      },
      assignment_id: {
        type: 'dropdown', label: 'Assignment ID', required: true,
        source: 'ASSIGNMENT', valueField: 'assignment_id', labelField: 'employee_code',
        readOnly: true,
        autoFillFromFk: {
          exit_date: 'end_date'
        },
        hint: 'Auto-selected active assignment'
      },
      resignation_date: { type: 'date', label: 'Resignation Date', required: false, hint: 'Date the employee formally resigned' },
      notice_start_date: {
        type: 'date', label: 'Notice Start Date', required: false,
        hint: 'Date of resignation or termination notice',
        recomputeOnChange: true
      },
      notice_period_days: {
        type: 'number', label: 'Notice Period (Days)', required: false,
        hint: 'Auto-fetched from Payroll Relationship',
        recomputeOnChange: true
      },
      exit_date: {
        type: 'date', label: 'Exit Date', required: true,
        hint: 'Date of employee offboarding',
        recomputeOnChange: true,
        compute: (state) => {
          const startDateStr = state.notice_start_date || state.resignation_date;
          if (startDateStr && state.notice_period_days !== undefined && state.notice_period_days !== '') {
            const d = new Date(startDateStr);
            d.setDate(d.getDate() + Number(state.notice_period_days));
            return d.toISOString().split('T')[0];
          }
          return state.exit_date;
        }
      },
      linked_payroll_run_id: {
        ...FK.period,
        required: false,
        label: 'Linked Payroll Run',
        filterFn: (period) => {
          const status = String(period.status || '').toUpperCase();
          return status === 'PENDING' || status === 'OPEN';
        }
      },

      gross_settlement: { type: 'number', label: 'Gross Settlement (₹)', required: false },
      total_earnings: { type: 'number', label: 'Total Earnings (₹)', required: false, recomputeOnChange: true, compute: (state) => Number(state.total_earnings) || 0 },
      total_deductions: { type: 'number', label: 'Total Deductions (₹)', required: false, recomputeOnChange: true, compute: (state) => Number(state.total_deductions) || 0 },
      total_recovery: {
        type: 'number', label: 'Total Recovery (₹)', required: false,
        recomputeOnChange: true,
        compute: (state) => {
          if (!state.recovery_lines) return Number(state.total_recovery) || 0;
          try {
            const lines = typeof state.recovery_lines === 'string' ? JSON.parse(state.recovery_lines) : state.recovery_lines;
            if (!Array.isArray(lines)) return Number(state.total_recovery) || 0;
            const sum = lines.reduce((acc, curr) => {
              if (curr.recovery_method === 'Cash' || curr.recovery_method === 'Waive') return acc;
              return acc + (Number(curr.remaining_amount) || 0);
            }, 0);
            return Math.round(sum * 100) / 100;
          } catch (e) {
            return Number(state.total_recovery) || 0;
          }
        }
      },
      net_payable: {
        type: 'number', label: 'Net Payable (₹)', required: false,
        recomputeOnChange: true,
        compute: (state) => {
          const earnings = Number(state.total_earnings) || 0;
          const deductions = Number(state.total_deductions) || 0;

          let recovery = Number(state.total_recovery) || 0;
          if (state.recovery_lines) {
            try {
              const lines = typeof state.recovery_lines === 'string' ? JSON.parse(state.recovery_lines) : state.recovery_lines;
              if (Array.isArray(lines)) {
                recovery = lines.reduce((acc, curr) => {
                  if (curr.recovery_method === 'Cash' || curr.recovery_method === 'Waive') return acc;
                  return acc + (Number(curr.remaining_amount) || 0);
                }, 0);
              }
            } catch (e) { }
          }

          return Math.round((earnings - deductions - recovery) * 100) / 100;
        }
      },

      status: {
        type: 'select', label: 'Status', required: true, defaultValue: 'Pending',
        options: [
          { value: 'Draft', label: 'Draft' },
          { value: 'Pending', label: 'Pending' },
          { value: 'Processing', label: 'Processing' },
          { value: 'Approved', label: 'Approved' },
          { value: 'Paid', label: 'Paid' },
          { value: 'Closed', label: 'Closed' }
        ]
      },
      lifecycle_status: {
        type: 'select', label: 'Lifecycle Status', required: true, defaultValue: 'Initiated',
        options: [
          { value: 'Initiated', label: 'Initiated' },
          { value: 'Pending Clearance', label: 'Pending Clearance' },
          { value: 'Closed', label: 'Closed' }
        ]
      },
      recovery_lines: {
        type: 'custom-recoveries',
        label: 'Recovery Strategy'
      },
      ...AUTO,
    }
  },

  GRADE_MASTER: {
    pk: 'grade_id', pkPrefix: 'GRD', pkPad: 3, label: 'Grade Master', showPk: true,
    displayColumns: ['grade_id', 'grade_code', 'grade_name', 'COMPANY_id', 'active_flag'],
    sections: [
      { id: 'org', label: 'Organization Context', fields: ['bg_id', 'COMPANY_id'], columns: 2 },
      { id: 'details', label: 'Grade Details', fields: ['grade_code', 'grade_name', 'grade_level', 'min_salary', 'max_salary', 'description'], columns: 2 },
      { id: 'metadata', label: 'Status & Dates', fields: ['active_flag', 'effective_from'], columns: 2 }
    ],
    fields: {
      bg_id: {
        ...FK.bg,
        cascade: ['COMPANY_id'],
        hint: 'Select Business Group first — Company list will filter by this'
      },
      COMPANY_id: {
        ...FK.company,
        dependsOn: 'bg_id',
        filterBy: 'default_bg_id',
        hint: 'Only companies belonging to selected Business Group are shown'
      },
      grade_code: { type: 'text', label: 'Grade Code', required: true },
      grade_name: { type: 'text', label: 'Grade Name', required: true },
      grade_level: { type: 'number', label: 'Grade Level', required: false },
      min_salary: { type: 'number', label: 'Min Salary', required: false },
      max_salary: { type: 'number', label: 'Max Salary', required: false },
      description: { type: 'text', label: 'Description', required: false, multiline: true },
      active_flag: { type: 'flag', label: 'Status', required: true },
      effective_from: { type: 'date', label: 'Effective From', required: true },
      ...AUTO
    }
  },

};

// ── Auto-generate next PK ─────────────────────────────────────────────────────
export function generatePK(tableConfig, existingRecords = [], companyId = '') {
  const { pk, pkPrefix, pkPad } = tableConfig;
  const prefix = companyId ? `${companyId}-${pkPrefix}` : pkPrefix;

  const max = existingRecords.reduce((acc, r) => {
    const val = String(r[pk] || '');
    let rest = val;
    if (val.startsWith(prefix)) {
      rest = val.slice(prefix.length);
    } else if (val.startsWith(pkPrefix)) {
      rest = val.slice(pkPrefix.length);
    }
    rest = rest.replace(/^[^0-9]+/, '');
    const num = parseInt(rest, 10);
    return isNaN(num) ? acc : Math.max(acc, num);
  }, 0);
  return prefix + String(max + 1).padStart(pkPad, '0');
}
